/**
 * Event Handler Module for Murphy Door Configurator
 *
 * Centralizes event handling by:
 * - Managing all user interactions
 * - Coordinating event delegation
 * - Handling event propagation
 * - Managing event state transitions
 * - Coordinating UI updates through main conductor
 * - Dispatching state change events
 * - Managing multi-variant product selection flow
 * - Coordinating variant selection events
 * - Managing event batching and debouncing
 * - Coordinating state-driven event responses
 * - Maintaining event-state synchronization
 *
 * Dependencies:
 * - md-config-core-config.js (data structures)
 * - md-config-core-conductor.js (conductor)
 * - STATE_MAPS (state management)
 * - STATE_ACTIONS (event types)
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // PATTERN #16: Initialization Guard (10 points)
  let isInitialized = false;

  // PATTERN #15: Cleanup Arrays (10 points)
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN #6: Module-Load-Time Bootstrap Subscription (10 points)
  // ============================================================================
  // Subscribe at module load time for race protection.
  // NOTE: This module's setupEventListeners() is called by the conductor AFTER
  // bootstrap completes, so this is defensive. The flag enables future guards
  // if any event subscriptions are added that could fire pre-bootstrap.
  let bootstrapComplete = false;

  // Centralized cleanup function (DRY: single source of truth for teardown logic)
  // Called by both the public cleanup() method and SYSTEM_SHUTDOWN handler
  function _internalCleanup() {
    isDev() && console.log('[EventHandler] Cleaning up resources');
    cleanupHandlers.forEach((unsub) => {
      if (typeof unsub === 'function') unsub();
    });
    cleanupHandlers.length = 0;
    isInitialized = false;
    bootstrapComplete = false;
  }

  // ==========================================================================
  // SINGLE-VARIANT CLICK ROUTING (pure, module-scope so it is unit-testable
  // without booting the handler -- same reasoning as the conductor's
  // shouldClickApplyDecision).
  //
  // Returns the id of a product's ONLY variant, or null when the product has
  // any other shape. A thumbnail with a product id and no variant id is routed
  // by SUB-PATH 3 as "open the variant grid"; when this returns an id there is
  // no grid to open and the click must select that variant directly. See the
  // call site for the full failure mode.
  //
  // Deliberately does NOT consult ppd.productIdToVariantIdMap: that map is
  // restricted to variants titled 'Default Title' so automatic paths can never
  // silently choose a meaningfully-named variant. This runs only on an explicit
  // user click, which is precisely the consent that restriction demands, so it
  // matches on variant COUNT and ignores the title.
  // ==========================================================================
  function resolveSoleVariantId(product) {
    if (!product) return null;
    // CompositeProducts are assembled from several source products and are
    // never a one-variant pick; they are also routed to SUB-PATH 2 before
    // reaching here.
    if (product.type === 'CompositeProduct') return null;
    const variants = product.variants;
    if (!Array.isArray(variants) || variants.length !== 1) return null;
    const only = variants[0];
    if (!only || only.id === undefined || only.id === null || only.id === '') {
      return null;
    }
    return String(only.id);
  }

  // ==========================================================================
  // DOES CLICKING THIS TILE MEAN "OPEN ITS OPTIONS"? (pure, module-scope)
  //
  // True only for the tile of an ordinary product with more than one variant:
  // there is nothing to apply at that level, so the click can only drill in.
  //
  // Exists for the "second click = apply" shortcut, which keys off the teal
  // --active ring. In click-apply mode the back arrow now returns to the product
  // grid WITHOUT unapplying, and syncAppliedTileRings rings the product tile
  // that holds the answer (Custom Jamb Depth, Electromagnetic Lock). Without
  // this carve-out, clicking that ringed tile re-applied the answer the
  // customer already had instead of re-opening its options -- a dead tile.
  //
  // Every other tile keeps the shortcut exactly as before: variant tiles,
  // single-variant products, composite groups (their answer is auto-matched,
  // not browsed), and anything the dictionary cannot resolve.
  // ==========================================================================
  function opensVariantGridDecision(tile, product) {
    const t = tile || {};
    if (t.variantId || !t.productId || t.isSingleVariant) return false;
    if (t.isComposite || String(t.productId).startsWith('composite-')) {
      return false;
    }
    if (!product || product.type === 'CompositeProduct') return false;
    return Array.isArray(product.variants) && product.variants.length > 1;
  }

  // ==========================================================================
  // DOES A RE-TAP OF THE RINGED TILE MEAN "YES, THIS ONE — WHAT'S NEXT"?
  // (pure, module-scope)
  //
  // Runs only INSIDE the existing `_isRingedTile && !_ringedTileOpensGrid &&
  // !_simplifiedAncestor` branch, i.e. the path whose entire effect was to
  // re-apply the selection the customer already had. In apo_style + click-apply
  // that re-apply is invisible: the apply animation plays, the same variant is
  // written back, the price does not move and nothing advances. Clarity shows
  // customers doing it anyway, which reads as "I'm confirming this".
  //
  // So in that ONE configuration the re-tap becomes a confirm-and-continue.
  // Everywhere else it stays byte-identical:
  //
  //   - classic / dual_* -> false. Those experiences have an APPLY button and
  //     a real browsing state; a second click there is the documented
  //     second-click-to-apply shortcut and must keep applying.
  //   - click-apply off  -> false. Same reason: without click-apply the second
  //     click IS the apply.
  //   - composite groups -> false. Their answer is auto-matched from Wood &
  //     Finish rather than browsed, so "confirm this one" is not a gesture the
  //     customer can meaningfully make on the group tile, and re-applying is
  //     what re-runs the material match.
  //
  // The multi-variant product tile that opens its own grid never reaches here
  // (opensVariantGridDecision already excluded it) — that carve-out is still
  // the thing standing between this and a dead Custom Jamb Depth tile.
  // ==========================================================================
  function confirmAndContinueDecision(opts) {
    const o = opts || {};
    if (o.experience !== 'apo_style') return false;
    if (o.clickApplyEnabled !== true) return false;
    if (o.isComposite) return false;
    if (String(o.productId || '').indexOf('composite-') === 0) return false;
    return true;
  }

  // Exposed for unit testing the routing decision without booting the handler.
  window.MurphyDoorConfigurator._eventHandlerTestHelpers = {
    resolveSoleVariantId,
    opensVariantGridDecision,
    confirmAndContinueDecision,
  };

  if (
    window.MurphyDoorConfigurator?.CoreEventBus &&
    CONSTANTS?.EVENTS?.SYSTEM_STATE_FROM_PPD_READY
  ) {
    const unsubBootstrap = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        isDev() &&
          console.log(
            '[EventHandler] Bootstrap complete (SYSTEM_STATE_FROM_PPD_READY received)'
          );
      }
    );
    cleanupHandlers.push(unsubBootstrap);
  }

  // PATTERN #4: Subscribe to SYSTEM_SHUTDOWN for cleanup coordination
  if (
    window.MurphyDoorConfigurator?.CoreEventBus &&
    CONSTANTS?.EVENTS?.SYSTEM_SHUTDOWN
  ) {
    const unsubShutdown = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_SHUTDOWN,
      _internalCleanup
    );
    cleanupHandlers.push(unsubShutdown);
  }

  // ============================================================================
  // STANDARD EVENT PAYLOAD STRUCTURE
  // ============================================================================

  // ALL events published by event handler MUST include these required fields:
  // {
  //   timestamp: Date.now(),                        // REQUIRED - Event timestamp
  //   source: 'setupGlobalEventDelegation',         // REQUIRED - Source method
  //   handler: 'back_button_handler',               // REQUIRED - Handler name
  //
  //   // Context fields (include when applicable):
  //   categoryId: 'size',                           // When operating on category
  //   productId: '123456',                          // When operating on product
  //   variantId: '789012',                          // When operating on variant
  //   tabId: 'style',                               // When operating on tab
  //
  //   // Result fields (for completed events):
  //   executionTime: 15.43,                         // Handler execution time in ms
  //
  //   // Error fields (for failed events):
  //   error: 'Error message',                       // Error description
  //
  //   // Handler-specific data (as needed):
  //   isComplete: true,                             // Example from category handler
  //   selectionType: 'composite',                   // Example from thumbnail handler
  //   hasVariantId: true,                           // Example from apply handler
  // }

  const PERFORMANCE_THRESHOLDS = {
    EVENT_SETUP: 50, // ms - event listener setup threshold
    DELEGATION_SETUP: 20, // ms - delegation setup threshold
    HANDLER_EXECUTION: 15, // ms - individual handler threshold
  };

  const INTERACTION_CONSTANTS = {
    ADD_TO_CART_COOLDOWN: 2000, // ms - debounce cooldown for cart actions
  };

  const SELECTORS = {
    THUMBNAIL: '.murphy-door-configurator__thumbnail-item',
    APPLY_BUTTON: '.murphy-door-configurator__apply-button',
    BACK_BUTTON: '.murphy-door-configurator__back-button',
    CATEGORY_BUTTON: '.murphy-door-configurator__category-button',
    MODAL: '.murphy-door-configurator__modal',
    SHARE_BUTTON: '.murphy-door-configurator__share-button',
    MODAL_CLOSE: '.murphy-door-configurator__modal-close',
    CLEAR_BUTTON: '.murphy-door-configurator__clear-button',
    TAB_BUTTONS: '.murphy-door-configurator__tabs__tab-button',
    VARIANT: '.murphy-door-configurator__variant-item',
    CATEGORY_TITLE_WRAPPER: '.murphy-door-configurator__category-title-wrapper',
    THUMBNAILS: '.murphy-door-configurator__thumbnails',
    VARIANT_VIEW: '.murphy-door-configurator__variant-view',
  };

  window.MurphyDoorConfigurator.eventHandler = {
    /**
     * Sets up event listeners for the configurator following the Central Conductor Pattern.
     *
     * This method serves as the entry point for event handler initialization and follows
     * the Central Conductor Pattern by:
     * 1. Delegating to specialized methods for different event types
     * 2. Publishing event initialization events for system monitoring
     * 3. Implementing proper performance tracking
     * 4. Providing robust error handling
     *
     * The pattern ensures separation of concerns by delegating UI event handling to this
     * specialized module while maintaining centralized state management through the main conductor.
     *
     * @fires ui:event_listeners_initialized - When event listeners are successfully set up
     * @fires system:error - When event listener setup encounters an error
     * @returns {void}
     */
    setupEventListeners() {
      // PATTERN #16: Initialization Guard - prevent duplicate listener registration
      if (isInitialized) {
        isDev() &&
          console.warn(
            '[EventHandler] Already initialized, skipping duplicate setupEventListeners() call'
          );
        return;
      }

      const METHOD_SIGNATURE = 'setupEventListeners';
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // Publish initialization event
        this.publishEvent(CONSTANTS.EVENTS.UI_EVENT_SETUP_STARTED, {
          timestamp: startTime,
          method: METHOD_SIGNATURE,
          module: 'eventHandler',
        });

        // Set up global event delegation (primary event handling approach)
        this.setupGlobalEventDelegation();

        // NOTE: UI_TAB_ACTIVATION_REQUESTED is handled exclusively by navigation-manager.js (UI module).
        // Navigation-manager owns tab activation (DOM + categories) and delegates state tracking
        // to the conductor via handleStateChange('TAB_CHANGED'). Previously, event-handler also
        // subscribed here, causing duplicate updateTabDisplay() calls on swipe navigation.

        // Track performance and publish success event
        const setupDuration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;

        // Publish success event
        this.publishEvent(CONSTANTS.EVENTS.UI_EVENT_LISTENERS_INITIALIZED, {
          timestamp: window.performance ? window.performance.now() : Date.now(),
          method: METHOD_SIGNATURE,
          module: 'eventHandler',
          metrics: {
            setupDuration: setupDuration,
          },
        });

        // PATTERN #16: Mark as initialized after successful setup
        isInitialized = true;

        // Publish performance metric for monitoring
        if (setupDuration > PERFORMANCE_THRESHOLDS.EVENT_SETUP) {
          // Only track if setup took longer than threshold
          this.publishEvent(CONSTANTS.EVENTS.PERFORMANCE_EVENT_SETUP, {
            timestamp: window.performance
              ? window.performance.now()
              : Date.now(),
            method: METHOD_SIGNATURE,
            module: 'eventHandler',
            executionTime: setupDuration,
            threshold: PERFORMANCE_THRESHOLDS.EVENT_SETUP,
          });
        }
      } catch (error) {
        // Log error to console for debugging

        // Route through centralized error handler (DRY: single publication point)
        window.MurphyDoorConfigurator.utils.handleSystemError({
          type: 'EventHandlerError',
          error: error,
          message: 'Failed to set up event listeners',
          source: 'eventHandler.setupEventListeners',
          recoverable: false,
          impact: 'critical',
          details: {
            method: METHOD_SIGNATURE,
            module: 'eventHandler',
            executionTime:
              (window.performance ? window.performance.now() : Date.now()) -
              startTime,
          },
        });

        // Attempt to notify main conductor of the error for potential recovery
        try {
          if (
            window.MurphyDoorConfigurator.main &&
            typeof window.MurphyDoorConfigurator.main.handleError === 'function'
          ) {
            window.MurphyDoorConfigurator.main.handleError(
              'Event listener setup failed',
              error,
              {
                method: METHOD_SIGNATURE,
                module: 'eventHandler',
                severity: 'high',
                recoverable: false,
              }
            );
          }
        } catch (handlerError) {
          // Last resort error handling if even the error handler fails
        }
      }
    },

    /**
     * Sets up global event delegation for the configurator following the Central Conductor Pattern.
     *
     * This method implements an efficient event delegation pattern where all UI interactions
     * are captured at the document level, analyzed, and then dispatched to the main conductor
     * for centralized state management. This approach follows the Central Conductor Pattern by:
     *
     * 1. Using a single document-level event listener to handle all clicks
     * 2. Delegating state changes to the main conductor via handleStateChange()
     * 3. Preventing direct DOM manipulation outside of the UI module
     * 4. Maintaining clear separation of concerns between event handling and state management
     * 5. Publishing events for error handling and performance monitoring
     *
     * The method handles these interaction types:
     * - Back navigation (back buttons, selected multi-variant product clicks)
     * - Product/variant selection (thumbnails, single-variant, multi-variant, and direct variants)
     * - Selection application (apply buttons)
     * - Tab navigation (tab button clicks)
     * - Category toggles (category button clicks)
     * - Cart actions (add to cart button clicks)
     *
     * @fires ui:event_delegation_initialized - When event delegation is successfully set up
     * @fires performance:event_handler_slow - When an event handler execution exceeds performance thresholds
     * @fires system:error - When event delegation setup encounters an error
     * @returns {void}
     */
    setupGlobalEventDelegation() {
      const METHOD_SIGNATURE = 'setupGlobalEventDelegation';
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();

      try {
        // Publish initialization event
        this.publishEvent(CONSTANTS.EVENTS.UI_EVENT_DELEGATION_STARTED, {
          timestamp: startTime,
          method: METHOD_SIGNATURE,
          module: 'eventHandler',
        });

        // P6 A11Y: keyboard activation for the ARIA controls the navigation
        // manager stamps at creation — role="tab" buttons (roving tabindex +
        // arrow-key nav) and role="button" cards (answered gate cards;
        // real category headers are native buttons and need no key shim).
        // Activation FUNNELS THROUGH the existing click path via a synthetic
        // click, so there is a single source of truth for what a tap/click does.
        document.addEventListener('keydown', (event) => {
          try {
            const key = event.key;
            if (
              key !== 'Enter' &&
              key !== ' ' &&
              key !== 'Spacebar' &&
              key !== 'ArrowLeft' &&
              key !== 'ArrowRight' &&
              key !== 'ArrowUp' &&
              key !== 'ArrowDown' &&
              key !== 'Home' &&
              key !== 'End'
            ) {
              return;
            }

            const target = event.target;
            if (!target || typeof target.closest !== 'function') return;

            // --- role="tab" roving arrow-key navigation ---------------------
            const tab = target.closest('[role="tab"].murphy-tab-button');
            if (tab) {
              const tablist = tab.closest('[role="tablist"]');
              if (
                tablist &&
                (key === 'ArrowLeft' ||
                  key === 'ArrowRight' ||
                  key === 'ArrowUp' ||
                  key === 'ArrowDown' ||
                  key === 'Home' ||
                  key === 'End')
              ) {
                const tabs = Array.prototype.slice.call(
                  tablist.querySelectorAll('[role="tab"].murphy-tab-button')
                );
                if (tabs.length) {
                  const current = tabs.indexOf(tab);
                  let next = current;
                  if (key === 'Home') next = 0;
                  else if (key === 'End') next = tabs.length - 1;
                  else if (key === 'ArrowLeft' || key === 'ArrowUp')
                    next = (current - 1 + tabs.length) % tabs.length;
                  else next = (current + 1) % tabs.length;
                  event.preventDefault();
                  const nextTab = tabs[next];
                  if (nextTab && nextTab !== tab) {
                    nextTab.focus();
                    // Follow-focus activation via the existing click path.
                    nextTab.click();
                  }
                  return;
                }
              }
              if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
                event.preventDefault();
                tab.click();
                return;
              }
              return;
            }

            // --- role="button" card (Enter / Space) -----------------------
            // Since 2026-09-28 only ANSWERED GATE cards still reach this: a
            // real category card is no longer focusable -- its header is a
            // native <button> (CONSTANTS.CATEGORY_HEADER) whose Enter/Space
            // already produce a click that bubbles into the card's click
            // delegation, so `target === category` can never match for it.
            const category = target.closest(
              '.murphy-door-configurator__category-button'
            );
            if (
              category &&
              target === category &&
              (key === 'Enter' || key === ' ' || key === 'Spacebar')
            ) {
              event.preventDefault();
              category.click();
              return;
            }

            // --- role="button" option tile (Enter / Space) ----------------
            const optionTile = target.closest(
              '.murphy-door-configurator__variant-item[role="button"]'
            );
            if (
              optionTile &&
              target === optionTile &&
              (key === 'Enter' || key === ' ' || key === 'Spacebar')
            ) {
              event.preventDefault();
              optionTile.click();
            }
          } catch (err) {
            isDev() &&
              console.error(
                '❌ NAVIGATION KEYBOARD: keydown activation failed',
                err
              );
          }
        });

        document.addEventListener('click', (event) => {
          const handlerStartTime = window.performance
            ? window.performance.now()
            : Date.now();
          let handlerName = 'click_handler';

          try {
            // Custom Jamb Depth stepper card: the Inches/Fraction stepper
            // buttons and spinbutton value displays are wired directly by
            // md-config-ui-numeric-input-selector.js (click/keydown) and must
            // be left to native focus behavior and that module's own
            // listeners. MUST be checked FIRST -- with no earlier match, a
            // click here fell through everything below to the generic
            // category-button toggle handler, which closes an already-open
            // category on ANY unmatched click. Result (when this card was a
            // free-text <input>, pre-2026-09-11): tapping the input to start
            // typing dismissed the whole card before a customer could enter a
            // single digit. Guards the CARD (label/hint/steppers/message), not
            // just one control, so clicking the hint text or label has the
            // same safe no-op.
            //
            // 🚨 MUST EXCLUDE .numeric-input-placeholder-tile. That tile lives
            // INSIDE the card, and md-config-ui-numeric-input-selector.js's
            // commitEntry() applies a stepper value by calling
            // utils.simulateClick(tile) -- a real dispatched 'click' that is
            // meant to reach the ordinary tile-click handling further down in
            // this same delegation chain (the ordinary first-click-browses/
            // second-click-applies gate every other tile uses). An unqualified
            // card-wide guard swallows that synthetic click too, so the
            // stepper value is accepted but never actually applied --
            // surfaced live as "That didn't go through — please try again."
            // on every press. Caught during the original fix's own staging
            // verification, not by the unit tests (which build a static DOM
            // and never simulate an actual commitEntry() call).
            const numericInputCardMatch = event.target.closest(
              '.murphy-door-configurator__numeric-input-card'
            );
            if (
              numericInputCardMatch &&
              !event.target.closest(
                '.murphy-door-configurator__numeric-input-placeholder-tile'
              )
            ) {
              this._trackEventHandlerPerformance(
                'numeric_input_card_handler',
                handlerStartTime
              );
              return;
            }

            // Mobile metrics dashboard under the measurement sliders
            // (md-config-ui-metrics-dashboard.js): read-only rows plus one
            // "See it on the plan" button that module wires itself. Same
            // reason as the numeric-input card above: an unmatched click
            // inside a card falls through to the category toggle (collapses
            // an open classic card) or, in apo_style, to the card-header
            // "show me this card" scroll. Only the button is meant to act.
            if (event.target.closest('.murphy-door-configurator__metrics')) {
              this._trackEventHandlerPerformance(
                'metrics_dashboard_handler',
                handlerStartTime
              );
              return;
            }

            // Handle close icon click (X shown on open category buttons)
            // MUST be checked FIRST to prevent click bubbling to category toggle handler
            const pencilIconSpan = event.target.closest(
              '.murphy-door-configurator__pencil-edit-icon'
            );
            if (pencilIconSpan) {
              const category = pencilIconSpan.closest('[data-category]');
              const isCategoryOpen =
                category &&
                category.classList.contains(
                  'murphy-door-configurator__category-button--open'
                );
              if (isCategoryOpen) {
                handlerName = 'close_category_icon_handler';
                const categoryId = category.getAttribute('data-category');

                // Spin animation: rotate X icon, trigger close at midpoint so the
                // icon swap (X → pencil) coincides with the icon being edge-on (invisible)
                pencilIconSpan.classList.add(
                  'murphy-door-configurator__pencil-edit-icon--spinning'
                );
                pencilIconSpan.addEventListener(
                  'animationend',
                  () => {
                    pencilIconSpan.classList.remove(
                      'murphy-door-configurator__pencil-edit-icon--spinning'
                    );
                  },
                  { once: true }
                );

                if (categoryId) {
                  setTimeout(() => {
                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_CLOSE_CATEGORY_ICON_CLICKED,
                      {
                        categoryId,
                        timestamp: Date.now(),
                        source:
                          'setupGlobalEventDelegation.close_category_icon_handler',
                      }
                    );
                  }, 150);
                }
                this._trackEventHandlerPerformance(
                  handlerName,
                  handlerStartTime
                );
                return;
              }
              // Category is closed — fall through so the category button handler opens it
            }

            // Handle fullscreen exit button (close/minimize)
            const exitFullscreenBtn = event.target.closest(
              '[data-action="exit-fullscreen-configurator"]'
            );
            if (exitFullscreenBtn) {
              event.stopPropagation();
              handlerName = 'exit_fullscreen_handler';
              this.publishEvent(CONSTANTS.EVENTS.UI_FULLSCREEN_EXIT_REQUESTED, {
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation.exit_fullscreen_handler',
                trigger: 'close_button_click',
              });
              this._trackEventHandlerPerformance(handlerName, handlerStartTime);
              return;
            }

            // D1: dual-mode segmented control ("Most Popular" | "Advanced
            // options"). Publishes a mode-change request; the conductor persists
            // it and re-emits BUSINESS_CONFIGURATOR_MODE_CHANGED.
            const modeBtn = event.target.closest(
              '[data-action="set-configurator-mode"]'
            );
            if (modeBtn) {
              event.stopPropagation();
              const mode = modeBtn.getAttribute('data-mode');
              if (
                (mode === 'curated' || mode === 'full_control') &&
                window.MurphyDoorConfigurator?.CoreEventBus
              ) {
                this.publishEvent(
                  CONSTANTS.EVENTS.BUSINESS_CONFIGURATOR_MODE_CHANGE_REQUESTED,
                  {
                    mode: mode,
                    timestamp: Date.now(),
                    source: 'setupGlobalEventDelegation.mode_switch',
                  }
                );
              }
              this._trackEventHandlerPerformance(
                'configurator_mode_switch_handler',
                handlerStartTime
              );
              return;
            }

            // Answered gate card (e.g. "Cabinets?" collapsed to "Top Only"):
            // clicking the collapsed summary reopens it to change the
            // answer, same as clicking a completed real category reopens it.
            //
            // The original version of this branch relied on the assumption
            // stated in its own comment -- "once answered, .gate-card-options
            // is display:none so there's nothing to accidentally
            // double-handle". apo_style breaks that assumption on purpose:
            // gate options stay visible after answering
            // (section-md-apo-mode.css) so the answer can be changed in one
            // click. With the assumption gone, a click on an option bubbled
            // to the card, reopened the gate, and swallowed the selection --
            // the answer never changed and the customer had to click twice.
            // Live-reported by Daniel 2026-08-17 ("you have to press on the
            // thumbnail to be able to change options").
            //
            // So: a click that landed on an option button belongs to the
            // option branch below, not here. In classic/dual the options are
            // display:none while answered, so clickedGateOption can never be
            // truthy there and this is a no-op for them.
            const clickedGateOption = event.target.closest(
              '.murphy-door-configurator__gate-card-option'
            );
            const answeredGateCard = clickedGateOption
              ? null
              : event.target.closest(
                  '[data-category-gate][data-answered="true"]'
                );
            if (answeredGateCard) {
              event.stopPropagation();
              const gateId =
                answeredGateCard.getAttribute('data-category-gate');
              if (gateId && window.MurphyDoorConfigurator?.CoreEventBus) {
                this.publishEvent(CONSTANTS.EVENTS.UI_GATE_REOPEN_REQUESTED, {
                  gateId: gateId,
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation.gate_reopen',
                });
              }
              this._trackEventHandlerPerformance(
                'gate_reopen_handler',
                handlerStartTime
              );
              return;
            }

            // Gate cards (e.g. "Cabinets?"): synthetic question cards that
            // reveal/hide real category cards. Publishes an answer-change
            // request only — the conductor is the single point of allowlist
            // validation (via CATEGORY_VISIBILITY_DEPS), so this handler
            // does no per-gate validation of its own and needs no changes
            // when new gates are added.
            const gateBtn = event.target.closest(
              '[data-action="set-gate-answer"]'
            );
            if (gateBtn) {
              event.stopPropagation();

              // A greyed option is aria-disabled, not disabled -- the disabled
              // attribute would stop the browser firing the hover that shows the
              // title explaining WHY it is unavailable. Refusing the click here
              // is what makes that safe. Without this branch the option looks
              // unavailable and still answers the gate, which is worse than the
              // dead end it replaced.
              if (gateBtn.getAttribute('aria-disabled') === 'true') {
                this._trackEventHandlerPerformance(
                  'gate_answer_handler',
                  handlerStartTime
                );
                return;
              }

              const gateId = gateBtn.getAttribute('data-gate-id');
              const answer = gateBtn.getAttribute('data-answer');
              if (
                gateId &&
                answer &&
                window.MurphyDoorConfigurator?.CoreEventBus
              ) {
                this.publishEvent(
                  CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGE_REQUESTED,
                  {
                    gateId: gateId,
                    answer: answer,
                    timestamp: Date.now(),
                    source: 'setupGlobalEventDelegation.gate_answer',
                  }
                );
              }
              this._trackEventHandlerPerformance(
                'gate_answer_handler',
                handlerStartTime
              );
              return;
            }

            // P5: "Email me this design" — open the user's mail client with the
            // current shareable config URL (the address bar already carries the
            // ?config= after selections) in the body, BCC'ing sales when the
            // merchant configured a lead address. A human escape valve for
            // high-consideration buyers who want to share/save their build.
            const emailDesignBtn = event.target.closest(
              '[data-action="email-design"]'
            );
            if (emailDesignBtn) {
              event.preventDefault();
              event.stopPropagation();
              try {
                const configUrl = window.location.href;
                const salesBcc =
                  emailDesignBtn.getAttribute('data-sales-bcc') || '';
                const subject = encodeURIComponent('My Murphy Door design');
                const body = encodeURIComponent(
                  'Here is the door I configured:\n\n' +
                    configUrl +
                    '\n\nOpen the link to pick up where I left off, or reply with any questions.'
                );
                let mailto = 'mailto:?subject=' + subject + '&body=' + body;
                if (salesBcc) {
                  mailto += '&bcc=' + encodeURIComponent(salesBcc);
                }
                window.location.href = mailto;
              } catch (emailErr) {
                isDev() &&
                  console.warn(
                    '[EventHandler] email-design action failed:',
                    emailErr
                  );
              }
              this._trackEventHandlerPerformance(
                'email_design_handler',
                handlerStartTime
              );
              return;
            }

            // Handle unapply button clicks (× button on applied selection view)
            // MUST be checked BEFORE back navigation to prevent event bubbling to category toggle
            const unapplyButton = event.target.closest(
              '[data-action="unapply-selection"]'
            );
            if (unapplyButton) {
              event.stopPropagation();
              handlerName = 'unapply_selection_handler';

              const categoryId = unapplyButton.getAttribute('data-category-id');
              if (categoryId && window.MurphyDoorConfigurator?.CoreEventBus) {
                this.publishEvent(
                  CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
                  {
                    categoryId,
                    timestamp: Date.now(),
                    source: 'setupGlobalEventDelegation.unapply_handler',
                  }
                );
              }

              this._trackEventHandlerPerformance(handlerName, handlerStartTime);
              return;
            }

            // Handle back navigation
            // Clicking anywhere on the selected-product div (thumbnail, caption, status icon, etc.)
            // triggers back navigation — EXCEPT interactive elements like the save button
            const backTrigger =
              event.target.closest('.murphy-door-configurator__back-button') ||
              (!event.target.closest('.thumbnail-save-button') &&
                event.target.closest(
                  '.murphy-door-configurator__selected-multi-variant-product'
                ));
            if (backTrigger) {
              handlerName = 'back_button_handler';

              // Publish handler started event
              this.publishEvent(CONSTANTS.EVENTS.UI_BACK_BUTTON_CLICKED, {
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'back_button_handler',
              });

              const category = backTrigger.closest('[data-category]');
              if (category) {
                const categoryId = category.getAttribute('data-category');
                if (!categoryId) {
                  throw new Error('Missing category ID for back button action');
                }

                if (
                  window.MurphyDoorConfigurator.stateManager &&
                  typeof window.MurphyDoorConfigurator.stateManager
                    .clearVariantTransitionState === 'function'
                ) {
                  window.MurphyDoorConfigurator.stateManager.clearVariantTransitionState(
                    categoryId
                  );
                } else {
                  throw new Error(
                    'stateManager or clearVariantTransitionState method not available'
                  );
                }

                if (
                  window.MurphyDoorConfigurator.main &&
                  typeof window.MurphyDoorConfigurator.main
                    .handleStateChange === 'function'
                ) {
                  window.MurphyDoorConfigurator.main.handleStateChange(
                    'BACK_CLICKED',
                    {
                      categoryId,
                      timestamp: window.performance
                        ? window.performance.now()
                        : Date.now(),
                    }
                  );
                } else {
                  throw new Error(
                    'main.handleStateChange not available for back button action'
                  );
                }
              } else {
                isDev() &&
                  console.warn(
                    '[eventHandler] Back button clicked outside category context'
                  );
              }

              // Publish handler completed event
              const executionTime =
                (window.performance ? window.performance.now() : Date.now()) -
                handlerStartTime;
              this.publishEvent(CONSTANTS.EVENTS.UI_BACK_NAVIGATION_COMPLETED, {
                categoryId: category
                  ? category.getAttribute('data-category')
                  : null,
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'back_button_handler',
                executionTime: executionTime.toFixed(2),
              });

              // Track handler performance
              this._trackEventHandlerPerformance(handlerName, handlerStartTime);
              return;
            }

            // Handle apply button clicks with enhanced selection tracking
            const applyButton = event.target.closest(
              '.murphy-door-configurator__apply-button'
            );
            if (applyButton) {
              handlerName = 'apply_button_handler';

              try {
                // MILESTONE 1: Handler started
                this.publishEvent(CONSTANTS.EVENTS.UI_APPLY_BUTTON_CLICKED, {
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation',
                  handler: 'apply_button_handler',
                });

                const category = applyButton.closest('[data-category]');
                if (!category) {
                  throw new Error('Missing category for apply button action');
                }

                const categoryId = category.getAttribute('data-category');
                if (!categoryId) {
                  throw new Error(
                    'Missing category ID for apply button action'
                  );
                }

                // MILESTONE 2: State retrieval started
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_APPLY_SELECTION_STATE_RETRIEVAL_STARTED,
                  {
                    categoryId,
                    timestamp: Date.now(),
                  }
                );

                // ✅ STATE-BASED: Get active selection from state (O(1) lookup)
                // ELIMINATES: DOM query + redundant PPD lookup (productId already in state!)
                const activeSelectionData =
                  window.MurphyDoorConfigurator.main.getActiveSelectionFromState(
                    categoryId
                  );

                // MILESTONE 3: State retrieval completed
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_APPLY_SELECTION_STATE_RETRIEVED,
                  {
                    categoryId,
                    hasVariantId: !!activeSelectionData?.variantId,
                    hasProductId: !!activeSelectionData?.productId,
                    timestamp: Date.now(),
                  }
                );

                if (!activeSelectionData) {
                  throw new Error(
                    `No active selection found in category ${categoryId} for apply button`
                  );
                }

                const variantId = activeSelectionData.variantId;
                const productId = activeSelectionData.productId;

                if (!productId) {
                  throw new Error(
                    `Active selection state missing product ID in category ${categoryId}. State integrity issue.`
                  );
                }

                // MILESTONE 4: Validation passed
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_APPLY_SELECTION_VALIDATION_PASSED,
                  {
                    categoryId,
                    productId,
                    variantId: variantId || null,
                    timestamp: Date.now(),
                  }
                );

                // 🛡️ PREEMPTIVE FORBIDDEN CHECK: Block Apply if the browsed selection is forbidden.
                // This is the FINAL safety net - even if the thumbnail click wasn't blocked,
                // the Apply button MUST NOT apply a forbidden-disabled selection.
                {
                  const forbiddenValidator =
                    window.MurphyDoorConfigurator.forbiddenValidator;
                  if (
                    forbiddenValidator &&
                    typeof forbiddenValidator.isVariantPreemptivelyForbidden ===
                      'function'
                  ) {
                    const applyForbiddenCheck =
                      forbiddenValidator.isVariantPreemptivelyForbidden(
                        variantId
                      ) ||
                      forbiddenValidator.isVariantPreemptivelyForbidden(
                        productId
                      );
                    if (applyForbiddenCheck) {
                      isDev() &&
                        console.log(
                          '🚫 APPLY BLOCKED: Selection is forbidden-disabled by preemptive validation',
                          {
                            categoryId,
                            productId,
                            variantId,
                            reason: applyForbiddenCheck.reasonText,
                          }
                        );

                      // Announce the refusal as a FACT on the bus. Until this
                      // existed the three preemptive guards returned silently, so
                      // a customer hammering a greyed option produced no event at
                      // all and the friction was unmeasurable. reasonText is NOT
                      // forwarded -- it is customer-facing free text.
                      this.publishEvent(
                        CONSTANTS.EVENTS.UI_FORBIDDEN_TILE_CLICK_REFUSED,
                        {
                          categoryId: categoryId || null,
                          variantId: variantId || null,
                          productId: productId || null,
                          reason: 'forbidden_apply_blocked',
                          timestamp: Date.now(),
                        }
                      );

                      // Publish a user-facing notification event so UI can show feedback
                      this.publishEvent(
                        CONSTANTS.EVENTS.UI_APPLY_SELECTION_FAILED,
                        {
                          error:
                            'Selection is forbidden: ' +
                            applyForbiddenCheck.reasonText,
                          categoryId,
                          productId,
                          variantId,
                          reason: 'preemptive_forbidden',
                          timestamp: Date.now(),
                        }
                      );

                      this._trackEventHandlerPerformance(
                        handlerName,
                        handlerStartTime
                      );
                      return;
                    }
                  }
                }

                isDev() &&
                  console.log(
                    `✅ APPLY BUTTON: State-based selection retrieval - category ${categoryId} → product ${productId}${variantId ? ` (variant ${variantId})` : ''}`
                  );

                // At this point, we should have both categoryId, productId, and potentially variantId.
                // No need for the original generic !productId check here as specific errors are thrown above.

                // MILESTONE 5: Delegating to conductor
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_APPLY_SELECTION_DELEGATING,
                  {
                    categoryId,
                    productId,
                    variantId: variantId || null,
                    timestamp: Date.now(),
                  }
                );

                if (
                  window.MurphyDoorConfigurator.main &&
                  typeof window.MurphyDoorConfigurator.main
                    .handleStateChange === 'function'
                ) {
                  // 🎯 APPLY ANIMATION: Animate circle-check on active thumbnail before state change
                  const activeThumbnail = category.querySelector(
                    '.murphy-door-configurator__thumbnail-item--active'
                  );
                  const applyPayload = {
                    categoryId,
                    productId,
                    variantId: variantId,
                    timestamp: window.performance
                      ? window.performance.now()
                      : Date.now(),
                  };

                  if (activeThumbnail) {
                    this._triggerThumbnailApplyAnimation(
                      activeThumbnail,
                      () => {
                        window.MurphyDoorConfigurator.main.handleStateChange(
                          'APPLY_SELECTION',
                          applyPayload
                        );
                      }
                    );
                  } else {
                    // No active thumbnail tile found. This is expected for slider-based categories
                    // (HorizontalSlider / LONELY_MULTI_VARIANT) that don't render tile thumbnails with
                    // the --active CSS class. The state manager already confirmed a valid BROWSING
                    // selection above, so proceed with apply directly (skip tile animation).
                    isDev() &&
                      console.log(
                        `[EventHandler] Apply in "${categoryId}": no tile thumbnail active (likely slider category). ` +
                          `State-confirmed selection (product ${productId}, variant ${variantId || 'none'}). ` +
                          `Applying directly without tile animation.`
                      );
                    window.MurphyDoorConfigurator.main.handleStateChange(
                      'APPLY_SELECTION',
                      applyPayload
                    );
                  }
                } else {
                  throw new Error(
                    'main.handleStateChange not available for apply selection action'
                  );
                }

                // MILESTONE 6: Handler completed
                const executionTime =
                  (window.performance ? window.performance.now() : Date.now()) -
                  handlerStartTime;
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_APPLY_SELECTION_COMPLETED,
                  {
                    categoryId,
                    productId,
                    variantId: variantId || null,
                    executionTime: executionTime.toFixed(2),
                    timestamp: Date.now(),
                  }
                );

                // Track handler performance
                this._trackEventHandlerPerformance(
                  handlerName,
                  handlerStartTime
                );
                return;
              } catch (error) {
                // MILESTONE 7: Handler failed
                this.publishEvent(CONSTANTS.EVENTS.UI_APPLY_SELECTION_FAILED, {
                  error: error.message,
                  timestamp: Date.now(),
                });
                throw error;
              }
            }

            // Handle thumbnail clicks with selection states
            const thumbnail = event.target.closest(
              '.murphy-door-configurator__thumbnail-item'
            );
            if (thumbnail) {
              handlerName = 'thumbnail_handler';

              // 🎯 MATERIAL FILTER CHECK: Early return if thumbnail is filtered out by material preferences
              if (thumbnail.classList.contains('material-filtered-out')) {
                isDev() &&
                  console.log(
                    '🚫 CLICK BLOCKED: Thumbnail filtered out by material preference',
                    {
                      variantId: thumbnail.getAttribute('data-variant-id'),
                      productId: thumbnail.getAttribute('data-product-id'),
                    }
                  );
                return;
              }

              // 🛡️ PREEMPTIVE FORBIDDEN CHECK (LAYER 1 - DOM class):
              // Early return if thumbnail has forbidden-disabled CSS class
              if (thumbnail.classList.contains('forbidden-disabled')) {
                isDev() &&
                  console.log(
                    '🚫 CLICK BLOCKED: Thumbnail is forbidden-disabled by preemptive validation (DOM class)',
                    {
                      variantId: thumbnail.getAttribute('data-variant-id'),
                      productId: thumbnail.getAttribute('data-product-id'),
                      reason: thumbnail.getAttribute('data-forbidden-reason'),
                      ruleName: thumbnail.getAttribute(
                        'data-forbidden-rule-name'
                      ),
                    }
                  );
                // See the apply-path guard above for why this is published.
                // categoryId comes off the tile's own [data-category] ancestor --
                // the same lookup this handler already does a few lines below for
                // the second-click-to-apply path, and it only runs on a REFUSED
                // click, never in a render loop.
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_FORBIDDEN_TILE_CLICK_REFUSED,
                  {
                    categoryId:
                      thumbnail
                        .closest('[data-category]')
                        ?.getAttribute('data-category') || null,
                    variantId: thumbnail.getAttribute('data-variant-id'),
                    productId: thumbnail.getAttribute('data-product-id'),
                    reason: 'forbidden_disabled_class',
                    timestamp: Date.now(),
                  }
                );
                return;
              }

              // 🛡️ PREEMPTIVE FORBIDDEN CHECK (LAYER 2 - In-memory state):
              // Even if the DOM class hasn't been applied yet (timing gap), check the
              // in-memory forbidden state. This catches the race condition where
              // thumbnails render AFTER the preemptive calculation but BEFORE the
              // UI_UPDATE_PREEMPTIVE_FORBIDDEN handler applies the CSS classes.
              {
                const clickVariantId =
                  thumbnail.getAttribute('data-variant-id');
                const clickProductId =
                  thumbnail.getAttribute('data-product-id');
                const forbiddenValidator =
                  window.MurphyDoorConfigurator.forbiddenValidator;
                if (
                  forbiddenValidator &&
                  typeof forbiddenValidator.isVariantPreemptivelyForbidden ===
                    'function'
                ) {
                  const forbiddenCheck =
                    forbiddenValidator.isVariantPreemptivelyForbidden(
                      clickVariantId
                    ) ||
                    forbiddenValidator.isVariantPreemptivelyForbidden(
                      clickProductId
                    );
                  if (forbiddenCheck) {
                    isDev() &&
                      console.log(
                        '🚫 CLICK BLOCKED: Thumbnail is forbidden-disabled by in-memory preemptive state',
                        {
                          variantId: clickVariantId,
                          productId: clickProductId,
                          reason: forbiddenCheck.reasonText,
                        }
                      );
                    // Also apply the CSS class now so it's visually obvious.
                    // This is the SECOND writer of forbidden-disabled — ui-updater's
                    // forbidden pass is the first. Both must set the assistive-tech
                    // state, or a tile disabled down this path stays announced as an
                    // ordinary available button. ui-updater's clear pass picks it up
                    // again on the way out, since that keys off the class.
                    thumbnail.classList.add('forbidden-disabled');
                    window.MurphyDoorConfigurator.utils.setAriaDisabled(
                      thumbnail,
                      true
                    );
                    thumbnail.setAttribute(
                      'data-forbidden-reason',
                      forbiddenCheck.reasonText
                    );
                    // See the apply-path guard above. This is ALSO the guard that
                    // catches PRODUCT-level tiles: they carry the visual-only
                    // 'forbidden-product-all-disabled' class, which no guard reads,
                    // and are refused here by the isVariantPreemptivelyForbidden
                    // (clickProductId) half of the check.
                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_FORBIDDEN_TILE_CLICK_REFUSED,
                      {
                        categoryId:
                          thumbnail
                            .closest('[data-category]')
                            ?.getAttribute('data-category') || null,
                        variantId: clickVariantId,
                        productId: clickProductId,
                        reason: 'forbidden_disabled_state',
                        timestamp: Date.now(),
                      }
                    );
                    return;
                  }
                }
              }

              // 🎯 SECOND-CLICK TO APPLY: If thumbnail is already in BROWSING state (--active),
              // treat second click as "apply this selection" instead of re-selecting.
              // SIMPLIFIED 3Q EXCEPTION: simplified categories stay in BROWSING until
              // BUILD MY DOOR is clicked — second-click-to-apply is suppressed entirely.
              const _simplifiedAncestor = thumbnail.closest(
                '[data-category][data-simplified-visible="true"]'
              );
              const _isRingedTile = thumbnail.classList.contains(
                'murphy-door-configurator__thumbnail-item--active'
              );
              // A ringed multi-variant product tile opens its options instead
              // (see opensVariantGridDecision). PATTERN 9: product data via
              // conductor delegation only; looked up only for ringed tiles.
              const _ringedTileOpensGrid =
                _isRingedTile &&
                opensVariantGridDecision(
                  {
                    variantId: thumbnail.getAttribute('data-variant-id'),
                    productId: thumbnail.getAttribute('data-product-id'),
                    isSingleVariant:
                      thumbnail.getAttribute('data-is-single-variant') ===
                      'true',
                    isComposite:
                      thumbnail.getAttribute('data-is-composite') === 'true',
                  },
                  window.MurphyDoorConfigurator.conductor?.getProductFromDictionary?.(
                    thumbnail.getAttribute('data-product-id')
                  ) || null
                );
              if (
                _isRingedTile &&
                !_ringedTileOpensGrid &&
                !_simplifiedAncestor
              ) {
                event.stopPropagation();
                // A RE-TAP OF THE RINGED TILE NOW CONFIRMS AND MOVES ON
                // (2026-09-19), in apo_style + click-apply only.
                //
                // It used to re-apply the identical selection: the apply
                // animation played, the same variant was written back, the
                // price did not move and nothing advanced. That re-apply is
                // also invisible to every analytics milestone — the reporter
                // dedupes config_option_applied per category+variant, so an
                // already-applied variant re-applying emits nothing at all.
                //
                // See confirmAndContinueDecision for exactly which
                // configurations are carved out; every one of them falls
                // through to the unchanged re-apply path below.
                const _ccCategoryEl = thumbnail.closest('[data-category]');
                const _ccCategoryId =
                  _ccCategoryEl?.getAttribute('data-category') || null;
                const _ccConductor = window.MurphyDoorConfigurator.conductor;
                if (
                  confirmAndContinueDecision({
                    experience:
                      _ccConductor?._getConfiguratorExperience?.() || null,
                    clickApplyEnabled:
                      _ccConductor?._isClickApplyEnabled?.() === true,
                    isComposite:
                      thumbnail.getAttribute('data-is-composite') === 'true',
                    productId: thumbnail.getAttribute('data-product-id'),
                  })
                ) {
                  // Feedback FIRST and locally: `pulseOnly` restarts the ring
                  // pulse on the tile the customer just touched WITHOUT moving
                  // the page. Their finger is already on that card, so a scroll
                  // here would be the "page moving under me" complaint Ryan
                  // filed four times in one UAT session. The page move, if any,
                  // is the coordinator's answer to the event below.
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_CATEGORY_FOCUS_REQUESTED,
                    {
                      categoryId: _ccCategoryId,
                      source: CONSTANTS.NAV_TAP_SOURCES.APPLIED_TILE,
                      pulseOnly: true,
                      timestamp: Date.now(),
                    }
                  );
                  // 🚨 NO APPLY. Not a re-apply, not an APPLY_SELECTION, not a
                  // _triggerThumbnailApplyAnimation. The selection is already
                  // exactly what the customer wants — that is the whole premise
                  // of this branch — so writing it again can only churn the
                  // cascade, the URL and the price for no change.
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_CONFIRM_AND_CONTINUE_REQUESTED,
                    {
                      categoryId: _ccCategoryId,
                      variantId: thumbnail.getAttribute('data-variant-id'),
                      source: CONSTANTS.NAV_TAP_SOURCES.APPLIED_TILE,
                      timestamp: Date.now(),
                    }
                  );
                  this._trackEventHandlerPerformance(
                    'thumbnail_confirm_and_continue',
                    handlerStartTime
                  );
                  return;
                }
                this._triggerThumbnailApplyAnimation(thumbnail, () => {
                  const category = thumbnail.closest('[data-category]');
                  const categoryId = category?.getAttribute('data-category');
                  const activeSelectionData =
                    window.MurphyDoorConfigurator.main?.getActiveSelectionFromState?.(
                      categoryId
                    );
                  if (
                    activeSelectionData &&
                    window.MurphyDoorConfigurator.main?.handleStateChange
                  ) {
                    window.MurphyDoorConfigurator.main.handleStateChange(
                      'APPLY_SELECTION',
                      {
                        categoryId,
                        productId: activeSelectionData.productId,
                        variantId: activeSelectionData.variantId || null,
                        timestamp: window.performance
                          ? window.performance.now()
                          : Date.now(),
                      }
                    );
                  } else {
                    const desyncError = `[EventHandler] Second-click apply: no active selection data or handleStateChange unavailable for category "${categoryId}". DOM/state desync.`;
                    isDev() && console.error(desyncError);
                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_APPLY_SELECTION_FAILED,
                      {
                        categoryId,
                        error: desyncError,
                        source: 'thumbnail_second_click_no_selection_data',
                        timestamp: Date.now(),
                      }
                    );
                  }
                });
                this._trackEventHandlerPerformance(
                  'thumbnail_second_click_apply',
                  handlerStartTime
                );
                return;
              }

              try {
                // MILESTONE 1: Handler started
                this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAIL_CLICKED, {
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation',
                  handler: 'thumbnail_handler',
                });

                event.stopPropagation();

                const variantId = thumbnail.getAttribute('data-variant-id');
                const productId = thumbnail.getAttribute('data-product-id');
                const productIsSingleVariant =
                  thumbnail.getAttribute('data-is-single-variant') === 'true';
                const category = thumbnail.closest('[data-category]');
                const categoryId = category?.getAttribute('data-category');

                // 🚀 UX FLAGS FIX: Check if category requires APPLY button (MIX categories)
                // Single-variant products in MIX categories should NOT auto-apply
                // because the category contains multi-variant products that need browsing state
                let isOneStep = productIsSingleVariant; // Default: single-variant = one-step

                if (productIsSingleVariant && categoryId) {
                  // Query UX flags from PPD via conductor delegation
                  const conductor = window.MurphyDoorConfigurator?.conductor;
                  if (
                    conductor &&
                    typeof conductor.getCategoryUXFlags === 'function'
                  ) {
                    const uxFlags = conductor.getCategoryUXFlags(categoryId);
                    if (uxFlags && uxFlags.requiresApplyButton) {
                      // 🎯 CRITICAL FIX: Category requires APPLY button (MIX category)
                      // Override one-step behavior - require browsing state
                      isOneStep = false;
                      isDev() &&
                        console.log(
                          `🎯 UX FLAGS: Category "${categoryId}" requires APPLY button - ` +
                            `single-variant product will enter BROWSING state (not auto-apply)`
                        );
                    }
                  }
                }

                // SUB-PATH 1: Handle single-variant product selection (auto-apply OR browsing)
                // 🎯 FIX: Check productIsSingleVariant instead of isOneStep so we handle BOTH cases:
                //   - isOneStep = true: Auto-apply single-variant (old behavior)
                //   - isOneStep = false: Browsing state with APPLY button (NEW - fixes "No Back Casing" bug)
                if (productIsSingleVariant) {
                  // MILESTONE 2: Single-variant detected
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_SINGLE_VARIANT_DETECTED,
                    {
                      productId,
                      timestamp: Date.now(),
                    }
                  );
                  if (!productId) {
                    throw new Error(
                      'Missing product ID for single-variant thumbnail action'
                    );
                  }

                  // Get conductor reference
                  const conductor = window.MurphyDoorConfigurator.conductor;

                  if (!conductor) {
                    throw new Error(
                      'Conductor not available for single-variant selection'
                    );
                  }

                  // BUBBLE-UP DETECTION: If this is a bubbled variant thumbnail, pass the
                  // pre-resolved variant ID directly (skips product selection step).
                  // For regular single-variant products, pass null to resolve via PPD.
                  const isBubbledVariant =
                    thumbnail.getAttribute('data-bubbled') === 'true';
                  const resolvedVariantId = isBubbledVariant ? variantId : null;

                  if (isBubbledVariant) {
                    isDev() &&
                      console.log(
                        `🫧 BUBBLE-UP CLICK: Product ${productId} bubbled to variant ${variantId} - direct selection`
                      );
                  }

                  // 🚀 DRY: Use unified selection coordinator for standardized BROWSING state entry
                  window.MurphyDoorConfigurator.selectionCoordinator
                    .enterBrowsingState(
                      {
                        productId,
                        categoryId,
                        element: thumbnail,
                        variantId: resolvedVariantId, // Bubbled: pre-resolved variant ID; Normal: null (resolve via PPD)
                        autoApplied: isOneStep,
                        isOneStep,
                      },
                      conductor
                    )
                    .then((result) => {
                      if (!result.success) {
                        isDev() &&
                          console.warn(
                            `[eventHandler] Single-variant selection coordination failed for product ${productId}. Reason: ${result.error}`
                          );
                        // Publish an event for the UI to provide feedback
                        this.publishEvent(
                          CONSTANTS.EVENTS.UI_SELECTION_FAILED,
                          {
                            productId: productId,
                            error: result.error,
                            source: 'single_variant_handler',
                          }
                        );
                      }
                    })
                    .catch((error) => {
                      isDev() &&
                        console.error(
                          `[eventHandler] Catastrophic failure during single-variant selection for product ${productId}.`,
                          error
                        );
                      this.publishEvent(CONSTANTS.EVENTS.UI_SELECTION_FAILED, {
                        productId: productId,
                        error: error.message || 'Unknown coordination error',
                        source: 'single_variant_handler_catch',
                      });
                    });

                  // 🔍 DEBUG: Log which path was taken
                  isDev() &&
                    console.log(
                      `✅ CLICK DEBUG: SUB-PATH 1 - Single-variant handler via coordinator (isOneStep: ${isOneStep ? 'auto-apply' : 'browsing with APPLY button'})`
                    );

                  // MILESTONE 3: Single-variant selection completed
                  const executionTime =
                    (window.performance
                      ? window.performance.now()
                      : Date.now()) - handlerStartTime;
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_COMPLETED,
                    {
                      productId,
                      selectionType: 'single_variant',
                      executionTime: executionTime.toFixed(2),
                      timestamp: Date.now(),
                    }
                  );

                  // Track handler performance
                  this._trackEventHandlerPerformance(
                    handlerName,
                    handlerStartTime
                  );
                  return;
                }

                // SUB-PATH 2: Handle CompositeProduct selection (grouped products) - same as multi-variant products
                // PPD-FIRST: Use product ID prefix as the authoritative composite indicator.
                // data-is-composite DOM attribute is unreliable when thumbnails are regenerated
                // from cached default HTML after material-filter re-rendering — the attribute
                // can be "false" even for genuine composite products. Product IDs starting with
                // 'composite-' are definitively composite per the PPD naming convention from
                // the composite preprocessor (Phase 1 Layer 2). This is a PPD-derived identity.
                //
                // IMPORTANT: Variant thumbnails INSIDE a composite variant grid also carry
                // data-product-id="composite-..." (the parent composite product's ID), but
                // they additionally have data-variant-id set. When variantId is present, the
                // user is selecting a specific variant — route to SUB-PATH 4 (direct variant
                // handler), NOT SUB-PATH 2 (composite group handler). Only treat as a composite
                // GROUP click when no specific variant is being targeted.
                const isCompositeAttr =
                  thumbnail.getAttribute('data-is-composite') === 'true';
                const isCompositePPD =
                  typeof productId === 'string' &&
                  productId.startsWith('composite-');
                const isComposite =
                  (isCompositeAttr || isCompositePPD) && !variantId;

                // 🔍 DEBUG: Log thumbnail attributes for debugging animation issue
                isDev() &&
                  console.log('🔍 CLICK DEBUG: Thumbnail attributes:', {
                    isComposite,
                    isCompositeAttr,
                    isCompositePPD,
                    'data-is-composite':
                      thumbnail.getAttribute('data-is-composite'),
                    'data-product-id': productId,
                    'data-group-id': thumbnail.getAttribute('data-group-id'),
                    'data-composite-id':
                      thumbnail.getAttribute('data-composite-id'),
                    categoryId: category?.getAttribute('data-category'),
                    allDataAttrs: Array.from(thumbnail.attributes)
                      .filter((attr) => attr.name.startsWith('data-'))
                      .map((attr) => `${attr.name}="${attr.value}"`)
                      .join(', '),
                  });

                if (isComposite) {
                  isDev() &&
                    console.log(
                      '✅ CLICK DEBUG: SUB-PATH 2 - CompositeProduct handler triggered (isCompositeAttr=' +
                        isCompositeAttr +
                        ', isCompositePPD=' +
                        isCompositePPD +
                        ')'
                    );
                  handlerName = 'composite_product_handler';

                  // PPD-FIRST: Prefer DOM attribute for groupId, fall back to deriving from productId.
                  // When thumbnails are rendered without composite-specific attributes (e.g., from
                  // default HTML that was re-rendered after material filter event), the productId
                  // still encodes the composite group as 'composite-{groupId}'. Strip the prefix
                  // and add 'group_' to match the PPD compositeGroupIdToProductMap key format.
                  const groupIdFromAttr =
                    thumbnail.getAttribute('data-group-id');
                  const groupId =
                    groupIdFromAttr ||
                    (isCompositePPD
                      ? 'group_' + productId.replace(/^composite-/, '')
                      : null);
                  const compositeId =
                    thumbnail.getAttribute('data-composite-id') || productId;

                  // MILESTONE 2: Composite detected
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_COMPOSITE_DETECTED,
                    {
                      groupId: groupId,
                      compositeId: compositeId,
                      timestamp: Date.now(),
                    }
                  );

                  if (!category) {
                    throw new Error(
                      'Missing category for CompositeProduct thumbnail action'
                    );
                  }

                  const categoryId = category.getAttribute('data-category');
                  if (!categoryId) {
                    throw new Error(
                      'Missing category ID for CompositeProduct thumbnail action'
                    );
                  }

                  if (!groupId) {
                    throw new Error(
                      'Missing required CompositeProduct group-id (could not derive from DOM or productId)'
                    );
                  }

                  // PPD-FIRST: Prefer DOM attribute for displayName, fall back to PPD lookup.
                  // When composite attributes are missing from thumbnail HTML, look up the
                  // composite product's display name from the conductor (PPD-authoritative).
                  const displayNameFromAttr =
                    thumbnail.getAttribute('data-display-name');
                  let displayName = displayNameFromAttr;
                  if (!displayName) {
                    const _conductorForName =
                      window.MurphyDoorConfigurator.conductor;
                    const _compositeLookup =
                      _conductorForName?.getCompositeProductByGroupId?.(
                        groupId
                      );
                    displayName =
                      _compositeLookup?.compositeGroupName ||
                      _compositeLookup?.displayName ||
                      groupId;
                  }

                  // MATERIAL PREFERENCE SHORTCUT: Check BEFORE publishing UI_PRODUCT_GROUP_CLICKED.
                  // When material preferences are active and resolve to exactly one variant for this
                  // composite product, skip the variant grid entirely and go directly to BROWSING state.
                  // This MUST run before UI_PRODUCT_GROUP_CLICKED to prevent MULTI_VARIANT_START from
                  // firing synchronously and rendering the variant grid before the shortcut can return early.
                  const conductor = window.MurphyDoorConfigurator.conductor;
                  const materialPrefs =
                    conductor?.getMaterialPreferences?.() || {};
                  const hasMaterialPrefs =
                    (materialPrefs.woodApplied &&
                      materialPrefs.appliedWoodSku) ||
                    (materialPrefs.finishApplied &&
                      materialPrefs.appliedFinishSku);

                  if (hasMaterialPrefs && conductor) {
                    const compositeProduct =
                      conductor.getCompositeProductByGroupId?.(groupId);
                    if (compositeProduct) {
                      // Look up filtered variants using the COMPOSITE product ID (not source product IDs).
                      // materialFilteredVariantMaps is keyed by PPD product IDs which use composite-{groupId}
                      // for grouped products. Source product IDs are NOT in the map.
                      const compositeProductId =
                        compositeProduct.id ||
                        compositeId ||
                        'composite-' + groupId;
                      const filtered =
                        conductor.getFilteredVariantsForProduct?.(
                          String(compositeProductId)
                        ) || [];

                      // getFilteredVariantsForProduct already filters by active preference VALUES
                      // (uses O(1) PPD byCombination/byWood/byFinish maps). We only need to exclude
                      // material-agnostic variants (no wood_sku AND no finish_sku, e.g., "No Back Casing")
                      // which the coordinator appends for compatibility but aren't material-specific matches.
                      const materialMatchedVariants = filtered.filter(
                        function (v) {
                          return v.wood_sku || v.finish_sku;
                        }
                      );

                      if (materialMatchedVariants.length === 1) {
                        const matchedVariant = materialMatchedVariants[0];
                        isDev() &&
                          console.log(
                            '🎯 MATERIAL SHORTCUT: Composite "' +
                              displayName +
                              '" resolved to single variant via material preferences - skipping variant grid',
                            {
                              variantId: matchedVariant.id,
                              title: matchedVariant.title,
                            }
                          );

                        // Use the unified selection coordinator to enter BROWSING state directly.
                        // Skip UI_PRODUCT_GROUP_CLICKED entirely — no need to trigger MULTI_VARIANT_START.
                        // productId fallback chain: sourceProductId is the canonical ID from composite
                        // variant preprocessing (Layer 2). productId is a fallback for enhanced variants
                        // from Layer 1. compositeId/groupId are last-resort context from the thumbnail.
                        window.MurphyDoorConfigurator.selectionCoordinator
                          .enterBrowsingState(
                            {
                              productId: String(
                                matchedVariant.sourceProductId ||
                                  matchedVariant.productId ||
                                  compositeId ||
                                  groupId
                              ),
                              categoryId: categoryId,
                              element: thumbnail,
                              variantId: String(matchedVariant.id),
                              autoApplied: false,
                              isOneStep: false,
                            },
                            conductor
                          )
                          .then(function (result) {
                            if (!result.success) {
                              isDev() &&
                                console.warn(
                                  '[eventHandler] Material shortcut browsing failed for composite "' +
                                    displayName +
                                    '". Reason: ' +
                                    result.error
                                );
                            }
                          })
                          .catch(function (error) {
                            isDev() &&
                              console.error(
                                '[eventHandler] Material shortcut catastrophic failure for composite "' +
                                  displayName +
                                  '".',
                                error
                              );
                          });

                        // Track performance and return (skip UI_PRODUCT_GROUP_CLICKED + MULTI_VARIANT_START)
                        this._trackEventHandlerPerformance(
                          handlerName,
                          handlerStartTime
                        );
                        return;
                      } else {
                        isDev() &&
                          console.log(
                            '🔍 MATERIAL SHORTCUT: Composite "' +
                              displayName +
                              '" has ' +
                              materialMatchedVariants.length +
                              ' material-matched variants - showing variant grid as normal'
                          );
                      }
                    }
                  }

                  // Publish ui:product_group_clicked event for CompositeProduct tracking.
                  // Only reached when the material shortcut did NOT resolve (0 or 2+ material-matched variants).
                  this.publishEvent(CONSTANTS.EVENTS.UI_PRODUCT_GROUP_CLICKED, {
                    groupId,
                    groupName: displayName,
                    categoryId,
                    compositeId,
                    element: thumbnail,
                    timestamp: window.performance
                      ? window.performance.now()
                      : Date.now(),
                  });

                  // Trigger MULTI_VARIANT_START state like regular multi-variant products for BROWSING behavior

                  if (
                    window.MurphyDoorConfigurator.main &&
                    typeof window.MurphyDoorConfigurator.main
                      .handleStateChange === 'function'
                  ) {
                    const stateData = {
                      productId: compositeId || groupId, // Use compositeId as productId, fallback to groupId
                      element: thumbnail,
                      categoryId,
                      isComposite: true, // Flag to identify composite products
                      groupId,
                      groupName: displayName,
                      displayMode: 'COMPOSITE_MULTI_VARIANT', // Required by conductor state validation
                      source: 'composite', // Indicates this is from composite product click
                      timestamp: window.performance
                        ? window.performance.now()
                        : Date.now(),
                    };

                    window.MurphyDoorConfigurator.main.handleStateChange(
                      'MULTI_VARIANT_START',
                      stateData
                    );
                  } else {
                    throw new Error(
                      'main.handleStateChange not available for CompositeProduct action'
                    );
                  }

                  // MILESTONE 3: Composite selection completed
                  const executionTimeComposite =
                    (window.performance
                      ? window.performance.now()
                      : Date.now()) - handlerStartTime;
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_COMPLETED,
                    {
                      groupId,
                      compositeId,
                      selectionType: 'composite',
                      executionTime: executionTimeComposite.toFixed(2),
                      timestamp: Date.now(),
                    }
                  );

                  // Track handler performance
                  this._trackEventHandlerPerformance(
                    handlerName,
                    handlerStartTime
                  );
                  return;
                }

                // SUB-PATH 3: Handle multi-variant product selection (product selection without variant)
                if (productId && !variantId) {
                  isDev() &&
                    console.log(
                      '✅ CLICK DEBUG: SUB-PATH 3 - Multi-variant product handler triggered'
                    );
                  handlerName = 'multi_variant_product_handler';

                  if (!category) {
                    throw new Error(
                      'Missing category for multi-variant product thumbnail action'
                    );
                  }

                  const categoryId = category.getAttribute('data-category');
                  if (!categoryId) {
                    throw new Error(
                      'Missing category ID for multi-variant product thumbnail action'
                    );
                  }

                  // ============================================================
                  // SINGLE-VARIANT SHORTCUT
                  // (sibling of the material shortcut in SUB-PATH 2 above)
                  //
                  // A thumbnail reaches SUB-PATH 3 whenever it carries a
                  // product id and no variant id -- which the tile builder does
                  // for every product it considers multi-variant. Its notion of
                  // "single variant" is the PPD's `isSingleVariant`
                  // (variantCount === 1 AND the variant is titled
                  // 'Default Title'), so a product with ONE meaningfully-named
                  // variant lands here and is treated as a grid to open.
                  //
                  // There is no grid to open. uiUpdater.showVariantLayout()
                  // refuses to render for `variantCount <= 1` and returns
                  // false, so MULTI_VARIANT_START sets
                  // variantTransitionStates[category] and then paints nothing
                  // -- and the `if (!transitionState)` guard further down makes
                  // EVERY later click in that category a silent no-op,
                  // including clicks on siblings that would have worked. The
                  // category is dead for the rest of the session with no error
                  // anywhere. Live on 2026-09-04 in apo_style: Jamb Depth,
                  // whose two standard products each hold exactly one variant
                  // titled "4-9/16 in." / "6-9/16 in.". apo_style never closes
                  // a category, so handleCategoryToggle's existing
                  // clearVariantTransitionState() safety net never fires and
                  // the brick is permanent.
                  //
                  // Resolve the one variant here and enter BROWSING directly.
                  // The conductor's click_apply chokepoint applies it in the
                  // same tick when that experience is on, exactly as it does
                  // for a normal variant tile.
                  //
                  // Why this does NOT just call the selection coordinator's
                  // _resolveVariantId: that reads ppd.productIdToVariantIdMap,
                  // which is deliberately restricted to 'Default Title'
                  // variants so cascade / URL-restore / auto-apply paths can
                  // never silently pick a meaningfully-named variant on the
                  // customer's behalf. A thumbnail click IS the explicit user
                  // selection that restriction exists to require, so resolving
                  // it here is safe where widening that map would not be. Do
                  // not "simplify" this by adding these products to the map.
                  // ============================================================
                  // PATTERN 9: product data via conductor delegation only.
                  const singleVariantConductor =
                    window.MurphyDoorConfigurator.conductor;
                  const singleVariantProduct =
                    singleVariantConductor &&
                    typeof singleVariantConductor.getProductFromDictionary ===
                      'function'
                      ? singleVariantConductor.getProductFromDictionary(
                          productId
                        )
                      : null;
                  const soleVariantId =
                    resolveSoleVariantId(singleVariantProduct);

                  if (
                    soleVariantId &&
                    window.MurphyDoorConfigurator.selectionCoordinator
                  ) {
                    isDev() &&
                      console.log(
                        '🎯 SINGLE-VARIANT SHORTCUT: product ' +
                          productId +
                          ' has exactly one variant (' +
                          singleVariantProduct.variants[0].title +
                          ') - selecting it directly instead of opening a variant grid'
                      );

                    handlerName = 'single_variant_product_handler';

                    window.MurphyDoorConfigurator.selectionCoordinator
                      .enterBrowsingState(
                        {
                          productId: String(productId),
                          categoryId: categoryId,
                          element: thumbnail,
                          variantId: soleVariantId,
                          autoApplied: false,
                          isOneStep: false,
                        },
                        singleVariantConductor
                      )
                      .then(function (result) {
                        if (!result || !result.success) {
                          isDev() &&
                            console.warn(
                              '[eventHandler] Single-variant shortcut browsing failed for product ' +
                                productId +
                                '. Reason: ' +
                                (result && result.error)
                            );
                        }
                      })
                      .catch(function (error) {
                        isDev() &&
                          console.error(
                            '[eventHandler] Single-variant shortcut catastrophic failure for product ' +
                              productId +
                              '.',
                            error
                          );
                      });

                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_COMPLETED,
                      {
                        productId,
                        categoryId,
                        variantId: soleVariantId,
                        selectionType: 'single_variant',
                        executionTime: (
                          (window.performance
                            ? window.performance.now()
                            : Date.now()) - handlerStartTime
                        ).toFixed(2),
                        timestamp: Date.now(),
                      }
                    );

                    this._trackEventHandlerPerformance(
                      handlerName,
                      handlerStartTime
                    );
                    return;
                  }

                  // MILESTONE 2: Multi-variant detected
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_MULTI_VARIANT_DETECTED,
                    {
                      productId,
                      categoryId,
                      timestamp: Date.now(),
                    }
                  );

                  let transitionState = null;
                  if (
                    window.MurphyDoorConfigurator.stateManager &&
                    typeof window.MurphyDoorConfigurator.stateManager
                      .getStateMaps === 'function'
                  ) {
                    const stateMaps =
                      window.MurphyDoorConfigurator.stateManager.getStateMaps();
                    if (stateMaps && stateMaps.variantTransitionStates) {
                      transitionState =
                        stateMaps.variantTransitionStates.get(categoryId);
                    } else {
                    }
                  } else {
                  }

                  if (!transitionState) {
                    if (
                      window.MurphyDoorConfigurator.main &&
                      typeof window.MurphyDoorConfigurator.main
                        .handleStateChange === 'function'
                    ) {
                      window.MurphyDoorConfigurator.main.handleStateChange(
                        'MULTI_VARIANT_START',
                        {
                          productId,
                          element: thumbnail,
                          categoryId,
                          timestamp: window.performance
                            ? window.performance.now()
                            : Date.now(),
                        }
                      );
                    } else {
                      throw new Error(
                        'main.handleStateChange not available for multi-variant product action'
                      );
                    }
                  }

                  // MILESTONE 3: Multi-variant selection completed
                  const executionTimeMulti =
                    (window.performance
                      ? window.performance.now()
                      : Date.now()) - handlerStartTime;
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_COMPLETED,
                    {
                      productId,
                      categoryId,
                      selectionType: 'multi_variant',
                      executionTime: executionTimeMulti.toFixed(2),
                      timestamp: Date.now(),
                    }
                  );

                  // Track handler performance
                  this._trackEventHandlerPerformance(
                    handlerName,
                    handlerStartTime
                  );
                  return;
                }

                // SUB-PATH 4: Handle direct variant selection (thumbnail with both product and variant IDs)
                if (variantId) {
                  handlerName = 'variant_selection_handler';

                  // MILESTONE 2: Direct variant detected
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_DIRECT_VARIANT_DETECTED,
                    {
                      variantId,
                      productId: productId || null,
                      timestamp: Date.now(),
                    }
                  );

                  let resolvedProductId = productId;

                  // If product ID is missing, resolve using O(1) PPD lookup
                  if (!resolvedProductId) {
                    // ✅ PPD-FIRST: Direct O(1) lookup using pre-built variantIdToProductIdMap
                    // ELIMINATES: Method call overhead + O(n²) search through all products and variants
                    // ARCHITECTURAL NOTE: This is an intentional trade-off, sacrificing abstraction for performance.
                    // This code is tightly coupled to the PPD's internal structure (`variantIdToProductIdMap`).
                    // Future refactors of the PPD must account for this direct access pattern.
                    const ppd = window.MurphyDoorConfigurator.main.getPPD();
                    if (!ppd.variantIdToProductIdMap) {
                      throw new Error(
                        'PPD variantIdToProductIdMap not initialized'
                      );
                    }
                    resolvedProductId = ppd.variantIdToProductIdMap.get(
                      variantId.toString()
                    );

                    if (!resolvedProductId) {
                      throw new Error(
                        `PPD Index Miss: Could not resolve product ID for variant ${variantId}. ` +
                          `Variant not found in variantIdToProductIdMap - possible data integrity issue.`
                      );
                    }

                    isDev() &&
                      console.log(
                        `✅ VARIANT SELECTION: Direct PPD O(1) variant→product lookup - variant ${variantId} → product ${resolvedProductId}`
                      );

                    // MILESTONE 3: Variant resolved
                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_THUMBNAIL_VARIANT_RESOLVED,
                      {
                        variantId,
                        resolvedProductId,
                        timestamp: Date.now(),
                      }
                    );
                  }

                  // Get conductor reference
                  const conductor = window.MurphyDoorConfigurator.conductor;

                  if (!conductor) {
                    throw new Error(
                      'Conductor not available for variant selection'
                    );
                  }

                  // Check if this variant belongs to a composite product
                  const groupId = thumbnail.getAttribute('data-group-id');
                  const groupName = thumbnail.getAttribute('data-group-name');
                  // Get categoryId from parent category element, not the variant
                  const parentCategory = thumbnail.closest('[data-category]');
                  const categoryId = parentCategory
                    ? parentCategory.getAttribute('data-category')
                    : null;

                  // 🚀 DRY: Use unified selection coordinator for standardized BROWSING state entry
                  window.MurphyDoorConfigurator.selectionCoordinator
                    .enterBrowsingState(
                      {
                        productId: resolvedProductId,
                        variantId,
                        categoryId,
                        element: thumbnail,
                        autoApplied: false,
                        compositeContext:
                          groupId && groupName
                            ? {
                                isCompositeProduct: true,
                                groupId,
                                groupName,
                                categoryId,
                                element: thumbnail,
                              }
                            : null,
                      },
                      conductor
                    )
                    .then((result) => {
                      if (!result.success) {
                        isDev() &&
                          console.warn(
                            `[eventHandler] Direct-variant selection coordination failed for product ${resolvedProductId}, variant ${variantId}. Reason: ${result.error}`
                          );
                        // Publish an event for the UI to provide feedback
                        this.publishEvent(
                          CONSTANTS.EVENTS.UI_SELECTION_FAILED,
                          {
                            productId: resolvedProductId,
                            variantId: variantId,
                            error: result.error,
                            source: 'direct_variant_handler',
                          }
                        );
                      }
                    })
                    .catch((error) => {
                      isDev() &&
                        console.error(
                          `[eventHandler] Catastrophic failure during direct-variant selection for variant ${variantId}.`,
                          error
                        );
                      this.publishEvent(CONSTANTS.EVENTS.UI_SELECTION_FAILED, {
                        productId: resolvedProductId,
                        variantId: variantId,
                        error: error.message || 'Unknown coordination error',
                        source: 'direct_variant_handler_catch',
                      });
                    });

                  // MILESTONE 4: Direct variant selection completed
                  const executionTimeVariant =
                    (window.performance
                      ? window.performance.now()
                      : Date.now()) - handlerStartTime;
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_COMPLETED,
                    {
                      variantId,
                      productId: resolvedProductId,
                      selectionType: 'direct_variant',
                      executionTime: executionTimeVariant.toFixed(2),
                      timestamp: Date.now(),
                    }
                  );

                  // Track handler performance
                  this._trackEventHandlerPerformance(
                    handlerName,
                    handlerStartTime
                  );
                  return;
                }
              } catch (error) {
                // Handler failed - publish failure event
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_THUMBNAIL_SELECTION_FAILED,
                  {
                    error: error.message,
                    timestamp: Date.now(),
                    handler: 'thumbnail_handler',
                  }
                );
                throw error; // Re-throw for outer catch
              }
            }

            // Handle tab navigation with progress validation
            const tab = event.target.closest(SELECTORS.TAB_BUTTONS);
            if (tab) {
              handlerName = 'tab_button_handler';

              // Publish handler started event
              this.publishEvent(CONSTANTS.EVENTS.UI_TAB_CLICKED, {
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'tab_button_handler',
              });

              const tabId = tab.getAttribute('data-tab');
              if (!tabId) {
                throw new Error('Missing tab ID for tab button action');
              }

              // Check for implicit apply eligibility before navigating
              const main = window.MurphyDoorConfigurator.main;
              if (
                main &&
                typeof main.evaluateImplicitApplyEligibility === 'function'
              ) {
                const implicitApplyResult =
                  main.evaluateImplicitApplyEligibility(tabId, 'tab');
                if (implicitApplyResult.eligible) {
                  this._handleImplicitApplyNavigation(
                    implicitApplyResult,
                    tabId,
                    'tab',
                    tab
                  );
                  this._trackEventHandlerPerformance(
                    handlerName,
                    handlerStartTime
                  );
                  return;
                }
              }

              if (
                window.MurphyDoorConfigurator.main &&
                typeof window.MurphyDoorConfigurator.main.handleStateChange ===
                  'function'
              ) {
                window.MurphyDoorConfigurator.main.handleStateChange(
                  'TAB_CHANGED',
                  {
                    tabId,
                    type: 'tab_activation',
                    autoOpenCategory: true,
                    source: CONSTANTS.EVENT_SOURCES.USER_CLICK, // Track that this was a manual user interaction
                    timestamp: window.performance
                      ? window.performance.now()
                      : Date.now(),
                  }
                );
              } else {
                throw new Error(
                  'main.handleStateChange not available for tab button action'
                );
              }

              // Publish handler completed event
              const executionTime =
                (window.performance ? window.performance.now() : Date.now()) -
                handlerStartTime;
              this.publishEvent(CONSTANTS.EVENTS.UI_TAB_CHANGE_COMPLETED, {
                tabId,
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'tab_button_handler',
                executionTime: executionTime.toFixed(2),
              });

              // Track handler performance
              this._trackEventHandlerPerformance(handlerName, handlerStartTime);
              return;
            }

            // 🔥 DUPLICATE REMOVAL: category_title_wrapper_handler removed
            // All category interactions (including title wrapper clicks) are now
            // handled by the unified category_button_handler below, which includes
            // all the functionality from the removed handler plus additional features

            // Handle category clicks with state validation
            const category = event.target.closest(SELECTORS.CATEGORY_BUTTON);
            if (category) {
              // ---- apo_style: a card is not a control ---------------------
              // A click on a non-gate card that is NOT on one of its controls
              // does NOTHING here, and returns so it cannot fall through to
              // the category toggle handler below. Cards never fold in
              // apo_style, so letting a stray click reach that handler would
              // collapse the one thing this mode guarantees stays open.
              //
              // 🚨 THIS USED TO UNAPPLY THE CATEGORY. Do not put that back.
              // Ryan, UAT 2026-08-27: removed deliberately.
              //
              // The reset was added 2026-08-17 ("clicking on the card div
              // resets that cards state and allows us to select a new
              // option") to serve the completed-card click SHIELD, which
              // covered the whole card so click one unapplied it and click
              // two picked the new option. That shield is gone -- see the
              // deleted-shield note in assets/section-md-apo-mode.css -- and
              // a tile click now replaces an applied selection on its own,
              // measured live. So the reset lost its only purpose while
              // keeping all of its blast radius: the card's title, its
              // "Wood Veneer | Warm Walnut | $0.00" summary line, its
              // "4 options" count and its padding were all still live wipe
              // targets, with no undo and (since apo_style hides the pencil)
              // no affordance saying so.
              //
              // Consequence, accepted by Ryan when this was removed: apo_style
              // has NO clear-a-category-back-to-empty gesture. A customer
              // switches options; they do not empty a category. Categories
              // that genuinely need "none" must offer it as a real option
              // tile, not rely on this click.
              //
              // Gate cards are still excluded wholesale: they carry
              // data-category-gate rather than data-category and own their
              // own reopen flow (UI_GATE_REOPEN_REQUESTED).
              const _apoConductor = window.MurphyDoorConfigurator.conductor;
              if (
                _apoConductor?._getConfiguratorExperience?.() === 'apo_style' &&
                !category.hasAttribute('data-category-gate')
              ) {
                // Option tiles are handled earlier in this delegation and
                // return before reaching here; they are listed anyway so this
                // stays correct if that ordering ever changes. Sliders matter
                // most: Width/Height render a drag control inside the card,
                // and a drag that ends in a click must not be read as a card
                // click.
                //
                // The header's own accordion toggle IS a native <button>
                // (WCAG 4.1.2 fix, 2026-09-28) but it is the card header, not
                // one of the card's controls: it must take the same
                // "show me this card" branch a header tap always took, never
                // fall through to the toggle handler that would collapse the
                // card. Hence the explicit exclusion.
                const headerToggle = event.target.closest(
                  CONSTANTS.CATEGORY_HEADER.TOGGLE_SELECTOR
                );
                const insideOptions =
                  !headerToggle &&
                  event.target.closest(
                    '.murphy-door-configurator__thumbnail-item,' +
                      '.murphy-door-configurator__variant-item,' +
                      '.murphy-door-configurator__slider-container,' +
                      '[role="slider"],[data-action],' +
                      'button,a,input,select,textarea,label'
                  );
                if (!insideOptions) {
                  // THE CARD HEADER NOW MEANS "SHOW ME THIS CARD" (2026-09-19).
                  //
                  // It still does NOT toggle and still does NOT unapply — both
                  // remain Ryan's 2026-08-27 decision and the `return` below is
                  // unchanged, so every guard above (gate cards, the numeric
                  // input card, anything inside `insideOptions`) keeps behaving
                  // exactly as it did. What changes is only that the tap is no
                  // longer SILENT: it asks ui-updater to bring this card fully
                  // into view under the sticky chrome and restart the ring
                  // pulse on whatever is applied here.
                  //
                  // 🚨 Deliberately NOT UI_CATEGORY_OPEN_REQUEST. That event is
                  // routed by navigation-manager and lands in
                  // md-config-bl-auto-navigation-coordinator.js's
                  // handleManualInteraction, which permanently sets
                  // `userHasManuallyInteracted` for every source but
                  // AUTO_NAVIGATOR — so wiring the header to it would have
                  // killed auto-navigation for the rest of the session the
                  // first time a customer tapped a card title. The dedicated
                  // UI_CATEGORY_FOCUS_REQUESTED has no such consumer.
                  this.publishEvent(
                    CONSTANTS.EVENTS.UI_CATEGORY_FOCUS_REQUESTED,
                    {
                      categoryId: category.getAttribute('data-category'),
                      source: CONSTANTS.NAV_TAP_SOURCES.CARD_HEADER,
                      timestamp: Date.now(),
                    }
                  );
                  this._trackEventHandlerPerformance(
                    'apo_category_noop_handler',
                    handlerStartTime
                  );
                  return;
                }
              }

              // Check if this is a programmatic click
              const programmaticData = event.programmaticData;
              const isProgrammatic =
                programmaticData &&
                programmaticData.categoryId ===
                  category.getAttribute('data-category');

              // Check if the click occurred inside the thumbnails or variant view
              // If so, we don't want to toggle the category when it's already open
              const thumbnailsOrVariant = event.target.closest(
                `${SELECTORS.THUMBNAILS}, ${SELECTORS.VARIANT_VIEW}`
              );

              // Only process the category click if:
              // 1. It's a programmatic click, OR
              // 2. The click is not inside thumbnails/variant view area, OR
              // 3. The category is not already open
              const isCategoryOpen = category.classList.contains(
                'murphy-door-configurator__category-button--open'
              );

              // FIXED: Allow thumbnail clicks in open categories for selection changes
              // Only block clicks that would interfere with category state, not thumbnail selections
              // Note: Thumbnail clicks are handled separately above, so they never reach this point anyway

              handlerName = 'category_button_handler';

              // Publish handler started event
              this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CLICKED, {
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'category_button_handler',
              });

              const categoryId = category.getAttribute('data-category');
              if (!categoryId) {
                throw new Error(
                  'Missing category ID for category button action'
                );
              }

              // Check for implicit apply eligibility before navigating
              // Only for non-programmatic clicks (programmatic clicks are system-driven)
              if (!isProgrammatic) {
                const mainRef = window.MurphyDoorConfigurator.main;
                if (
                  mainRef &&
                  typeof mainRef.evaluateImplicitApplyEligibility === 'function'
                ) {
                  const implicitApplyResult =
                    mainRef.evaluateImplicitApplyEligibility(
                      categoryId,
                      'category'
                    );
                  if (implicitApplyResult.eligible) {
                    this._handleImplicitApplyNavigation(
                      implicitApplyResult,
                      categoryId,
                      'category',
                      category
                    );
                    this._trackEventHandlerPerformance(
                      handlerName,
                      handlerStartTime
                    );
                    return;
                  }
                }
              }

              let isComplete = false;
              if (
                window.MurphyDoorConfigurator.main &&
                typeof window.MurphyDoorConfigurator.main
                  .checkCompletionStatus === 'function'
              ) {
                isComplete =
                  window.MurphyDoorConfigurator.main.checkCompletionStatus(
                    categoryId,
                    'category'
                  );
              } else {
              }

              if (
                window.MurphyDoorConfigurator.main &&
                typeof window.MurphyDoorConfigurator.main.handleStateChange ===
                  'function'
              ) {
                window.MurphyDoorConfigurator.main.handleStateChange(
                  'CATEGORY_TOGGLE',
                  {
                    categoryId,
                    type: 'category_activation',
                    isComplete,
                    programmatic: isProgrammatic,
                    timestamp: window.performance
                      ? window.performance.now()
                      : Date.now(),
                  }
                );
              } else {
                throw new Error(
                  'main.handleStateChange not available for category button action'
                );
              }

              // Publish handler completed event
              const executionTime =
                (window.performance ? window.performance.now() : Date.now()) -
                handlerStartTime;
              this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_TOGGLE_COMPLETED, {
                categoryId,
                isComplete,
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation',
                handler: 'category_button_handler',
                executionTime: executionTime.toFixed(2),
              });

              // Track handler performance
              this._trackEventHandlerPerformance(handlerName, handlerStartTime);
              return;
            }

            // Handle add to cart button clicks
            const addToCartButton = event.target.closest(
              '.murphy-door-configurator__add-to-cart-button'
            );
            if (addToCartButton) {
              handlerName = 'add_to_cart_handler';
              event.preventDefault();
              event.stopPropagation();

              // GUARD: Block clicks on disabled button (belt-and-suspenders safety)
              // The native disabled attribute prevents click events, but this guard
              // protects against edge cases with event delegation bubbling
              if (addToCartButton.disabled) {
                isDev() &&
                  console.log(
                    '🚫 EVENT HANDLER: ADD_TO_CART blocked - button is disabled (configuration incomplete)'
                  );
                this._trackEventHandlerPerformance(
                  handlerName,
                  handlerStartTime
                );
                return;
              }

              // DEBOUNCE PROTECTION: Prevent multiple rapid ADD_TO_CART events
              const now = Date.now();
              if (!this._lastAddToCartTime) {
                this._lastAddToCartTime = 0;
              }

              const timeSinceLastClick = now - this._lastAddToCartTime;
              if (
                timeSinceLastClick < INTERACTION_CONSTANTS.ADD_TO_CART_COOLDOWN
              ) {
                // 2 second cooldown
                isDev() &&
                  console.log(
                    '🚫 EVENT HANDLER: ADD_TO_CART debounced - too soon after previous click:',
                    timeSinceLastClick + 'ms'
                  );

                // Publish debounce event
                this.publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_DEBOUNCED, {
                  timeSinceLastClick,
                  threshold: INTERACTION_CONSTANTS.ADD_TO_CART_COOLDOWN,
                  timestamp: now,
                });

                this._trackEventHandlerPerformance(
                  handlerName,
                  handlerStartTime
                );
                return;
              }

              this._lastAddToCartTime = now;
              isDev() &&
                console.log(
                  '✅ EVENT HANDLER: ADD_TO_CART allowed - cooldown satisfied'
                );

              // Publish allowed event
              this.publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_ALLOWED, {
                timeSinceLastClick,
                threshold: INTERACTION_CONSTANTS.ADD_TO_CART_COOLDOWN,
                timestamp: now,
              });

              try {
                // Publish cart button clicked event
                this.publishEvent(CONSTANTS.EVENTS.UI_CART_BUTTON_CLICKED, {
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation',
                  handler: 'add_to_cart_handler',
                });

                if (
                  window.MurphyDoorConfigurator.main &&
                  typeof window.MurphyDoorConfigurator.main
                    .handleStateChange === 'function'
                ) {
                  window.MurphyDoorConfigurator.main.handleStateChange(
                    'ADD_TO_CART',
                    {
                      type: 'cart_action',
                      event: event,
                      timestamp: window.performance
                        ? window.performance.now()
                        : Date.now(),
                    }
                  );
                } else {
                  throw new Error(
                    'main.handleStateChange not available for add to cart action'
                  );
                }

                // Publish cart add completed event
                const executionTime =
                  (window.performance ? window.performance.now() : Date.now()) -
                  handlerStartTime;
                this.publishEvent(CONSTANTS.EVENTS.UI_CART_ADD_COMPLETED, {
                  executionTime: executionTime.toFixed(2),
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation',
                  handler: 'add_to_cart_handler',
                });

                // Track handler performance
                this._trackEventHandlerPerformance(
                  handlerName,
                  handlerStartTime
                );
                return;
              } catch (error) {
                // Publish cart add failed event
                this.publishEvent(CONSTANTS.EVENTS.UI_CART_ADD_FAILED, {
                  error: error.message,
                  timestamp: Date.now(),
                  source: 'setupGlobalEventDelegation',
                  handler: 'add_to_cart_handler',
                });
                throw error; // Re-throw for outer catch
              }
            }

            // INERT TAP #3/#4 — price_display / shipping_estimate.
            //
            // The live price and the shipping/delivery estimate still carry
            // `.murphy-door-info-icon`, which is what made them LOOK tappable;
            // their tooltip initializers were deleted in 9c1c9f84 (2026-02-18)
            // and the class was kept only because a compound layout rule in
            // section-md-configurator.css needs it (see the comment in
            // snippets/configurator-price-bar.liquid). So a tap here opens
            // nothing and never has.
            //
            // 🚨 DELIBERATELY LAST, after every real handler, and NOT at the
            // top with the numeric-input guard. Placement is behaviourally
            // identical today — none of these three elements is inside a
            // category button, a tile, or any other matched selector, so a tap
            // already fell all the way through to the generic-click tracker.
            // Last is future-proof: if a tooltip or any other affordance is
            // ever restored on these elements, a top-of-chain `return` would
            // silently swallow it, whereas from here the new handler matches
            // first and this branch simply stops firing — which is the correct
            // outcome, since the surface would no longer be inert.
            //
            // PUBLISH ONLY: no preventDefault, no stopPropagation, no state
            // change. The `return` is the same no-op the generic-click tracker
            // below performs, minus its perf sample.
            const inertReadout = event.target.closest(
              '.murphy-door-configurator__price-badge,' +
                '.murphy-door-configurator__price-group,' +
                '.murphy-door-configurator__shipping-estimate'
            );
            if (inertReadout) {
              this.publishEvent(CONSTANTS.EVENTS.UI_INERT_TAP_DETECTED, {
                surface: inertReadout.classList.contains(
                  'murphy-door-configurator__shipping-estimate'
                )
                  ? CONSTANTS.INERT_TAP_SURFACES.SHIPPING_ESTIMATE
                  : CONSTANTS.INERT_TAP_SURFACES.PRICE_DISPLAY,
                timestamp: Date.now(),
                source: 'setupGlobalEventDelegation.inert_readout',
              });
              this._trackEventHandlerPerformance(
                'inert_readout_handler',
                handlerStartTime
              );
              return;
            }

            // No specific handler matched - track as generic click
            this._trackEventHandlerPerformance(
              'generic_click_handler',
              handlerStartTime
            );
          } catch (error) {
            // Handle errors in event processing

            // Route through centralized error handler (DRY: single publication point)
            window.MurphyDoorConfigurator.utils.handleSystemError({
              type: 'EventHandlerError',
              error: error,
              message: `Failed to process click event in ${handlerName}`,
              source: 'eventHandler.setupGlobalEventDelegation',
              recoverable: true,
              impact: 'medium',
              details: {
                method: METHOD_SIGNATURE,
                handler: handlerName,
                module: 'eventHandler',
                executionTime:
                  (window.performance ? window.performance.now() : Date.now()) -
                  handlerStartTime,
              },
            });

            // Attempt to notify main conductor for potential recovery
            try {
              if (
                window.MurphyDoorConfigurator.main &&
                typeof window.MurphyDoorConfigurator.main.handleError ===
                  'function'
              ) {
                window.MurphyDoorConfigurator.main.handleError(
                  `Click event processing failed in ${handlerName}`,
                  error,
                  {
                    method: METHOD_SIGNATURE,
                    handler: handlerName,
                    module: 'eventHandler',
                    severity: 'medium',
                    recoverable: true,
                  }
                );
              }
            } catch (handlerError) {
              // Last resort error logging if even the error handler fails
              isDev() &&
                console.error(
                  `Critical error: Failed to handle ${handlerName} error:`,
                  handlerError
                );
            }
          }
        });

        // Publish success event
        const setupDuration =
          (window.performance ? window.performance.now() : Date.now()) -
          startTime;

        this.publishEvent(CONSTANTS.EVENTS.UI_EVENT_DELEGATION_INITIALIZED, {
          timestamp: window.performance ? window.performance.now() : Date.now(),
          method: METHOD_SIGNATURE,
          module: 'eventHandler',
          metrics: {
            setupDuration: setupDuration,
          },
        });

        // Publish performance metric for monitoring if setup was slow
        if (setupDuration > PERFORMANCE_THRESHOLDS.DELEGATION_SETUP) {
          this.publishEvent(
            CONSTANTS.EVENTS.PERFORMANCE_EVENT_DELEGATION_SETUP,
            {
              timestamp: window.performance
                ? window.performance.now()
                : Date.now(),
              method: METHOD_SIGNATURE,
              module: 'eventHandler',
              executionTime: setupDuration,
              threshold: PERFORMANCE_THRESHOLDS.DELEGATION_SETUP,
            }
          );
        }
      } catch (error) {
        // Handle setup errors
        isDev() &&
          console.error('Error setting up global event delegation:', error);

        // Route through centralized error handler (DRY: single publication point)
        window.MurphyDoorConfigurator.utils.handleSystemError({
          type: 'EventHandlerError',
          error: error,
          message: 'Failed to set up global event delegation',
          source: 'eventHandler.setupGlobalEventDelegation',
          recoverable: false,
          impact: 'critical',
          details: {
            method: METHOD_SIGNATURE,
            module: 'eventHandler',
            executionTime:
              (window.performance ? window.performance.now() : Date.now()) -
              startTime,
          },
        });

        // Attempt to notify main conductor for potential recovery
        try {
          if (
            window.MurphyDoorConfigurator.main &&
            typeof window.MurphyDoorConfigurator.main.handleError === 'function'
          ) {
            window.MurphyDoorConfigurator.main.handleError(
              'Global event delegation setup failed',
              error,
              {
                method: METHOD_SIGNATURE,
                module: 'eventHandler',
                severity: 'high',
                recoverable: false,
              }
            );
          }
        } catch (handlerError) {
          // Last resort error logging if even the error handler fails
          isDev() &&
            console.error(
              'Critical error: Failed to handle event delegation setup error:',
              handlerError
            );
        }
      }
    },

    // =========================================================================
    // IMPLICIT APPLY ON NAVIGATION HELPERS
    // =========================================================================

    /**
     * Orchestrates the implicit apply flow: announces to screen readers,
     * triggers SAVING animation on the browsing thumbnail, applies the selection,
     * then navigates to the destination.
     *
     * @private
     * @param {Object} eligibilityResult - Result from conductor.evaluateImplicitApplyEligibility()
     * @param {string} targetId - The target tab or category ID
     * @param {string} targetType - 'tab' or 'category'
     * @param {HTMLElement} triggerElement - The DOM element that was clicked
     */
    _handleImplicitApplyNavigation(
      eligibilityResult,
      targetId,
      targetType,
      triggerElement
    ) {
      const {
        browsingCategoryId,
        applyPayload,
        silentItems = [],
      } = eligibilityResult;
      const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

      // Apply any background BROWSING selections silently before the animated primary apply.
      // These are selections from categories the user visited but didn't explicitly save.
      // Each must succeed before we proceed — abort navigation if any silent apply fails.
      if (silentItems.length > 0) {
        const main = window.MurphyDoorConfigurator.main;
        if (!main || typeof main.handleStateChange !== 'function') {
          isDev() &&
            console.error(
              '[EventHandler] Cannot apply silent items - main.handleStateChange unavailable'
            );
          this.publishEvent(CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_FAILED, {
            browsingCategoryId: silentItems[0]?.applyPayload?.categoryId,
            targetId,
            targetType,
            error: 'Conductor not available for silent apply.',
            timestamp: Date.now(),
          });
          return;
        }
        let silentApplyFailed = false;
        for (const silentItem of silentItems) {
          const applyResult = main.handleStateChange(
            'APPLY_SELECTION',
            silentItem.applyPayload
          );
          if (!applyResult) {
            isDev() &&
              console.error(
                `[EventHandler] Silent implicit apply failed for category "${silentItem.applyPayload.categoryId}". Aborting navigation.`
              );
            this.publishEvent(CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_FAILED, {
              browsingCategoryId: silentItem.applyPayload.categoryId,
              targetId,
              targetType,
              error:
                'A background selection could not be applied, please review your choices.',
              timestamp: Date.now(),
            });
            silentApplyFailed = true;
            break;
          }
        }
        if (silentApplyFailed) return;
        isDev() &&
          console.log(
            `[EventHandler] Silently applied ${silentItems.length} background browsing selection(s) before navigation`
          );
      }

      // Resolve the browsing thumbnail from DOM (event-handler owns DOM access per CCP).
      // NOTE: '--active' is the established class for BROWSING state thumbnails throughout
      // this file (see existing apply handlers at lines ~516 and ~625). It is NOT a generic
      // hover/focus active state — it marks the currently previewed (BROWSING) selection.
      const browsingThumbnail = document.querySelector(
        `[data-category="${browsingCategoryId}"] .murphy-door-configurator__thumbnail-item--active`
      );

      // Announce to screen readers (WCAG aria-live polite)
      this._announceImplicitApply(browsingCategoryId);

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      // Publish started event - auto-navigation-coordinator subscribes to set suppressProgressNavigation
      this.publishEvent(CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_STARTED, {
        browsingCategoryId,
        targetId,
        targetType,
        timestamp: Date.now(),
      });

      if (prefersReducedMotion || !browsingThumbnail) {
        // Instant apply, then navigate
        this._performImplicitApplyInstant(
          applyPayload,
          targetId,
          targetType,
          triggerElement,
          browsingCategoryId
        );
      } else {
        // Animated apply (SAVING badge), then navigate after animation completes
        this._triggerThumbnailApplyAnimation(browsingThumbnail, () => {
          this._performImplicitApplyInstant(
            applyPayload,
            targetId,
            targetType,
            triggerElement,
            browsingCategoryId
          );
        });
      }
    },

    /**
     * Performs the instant implicit apply: applies the BROWSING selection,
     * then navigates to the destination on the next tick.
     *
     * @private
     * @param {Object} applyPayload - Ready-to-use payload for APPLY_SELECTION handleStateChange
     * @param {string} targetId - The target tab or category ID
     * @param {string} targetType - 'tab' or 'category'
     * @param {HTMLElement} triggerElement - The DOM element that was clicked
     * @param {string} browsingCategoryId - The category that had the BROWSING selection
     */
    _performImplicitApplyInstant(
      applyPayload,
      targetId,
      targetType,
      triggerElement,
      browsingCategoryId
    ) {
      const main = window.MurphyDoorConfigurator.main;

      if (!main || typeof main.handleStateChange !== 'function') {
        isDev() &&
          console.error(
            '[EventHandler] Cannot perform implicit apply - main.handleStateChange unavailable'
          );
        this.publishEvent(CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_FAILED, {
          browsingCategoryId,
          targetId,
          targetType,
          error: 'main.handleStateChange unavailable',
          timestamp: Date.now(),
        });
        return;
      }

      // Apply the browsing selection
      main.handleStateChange('APPLY_SELECTION', applyPayload);

      // Small tick to allow state updates to settle, then navigate to destination
      setTimeout(() => {
        if (targetType === 'tab') {
          main.handleStateChange('TAB_CHANGED', {
            tabId: targetId,
            type: 'tab_activation',
            autoOpenCategory: true,
            source: 'implicit_apply',
            timestamp: window.performance
              ? window.performance.now()
              : Date.now(),
          });
        } else {
          main.handleStateChange('CATEGORY_TOGGLE', {
            categoryId: targetId,
            type: 'category_activation',
            isComplete: false,
            programmatic: false,
            source: 'implicit_apply',
            timestamp: window.performance
              ? window.performance.now()
              : Date.now(),
          });
        }

        // Publish completion event
        this.publishEvent(CONSTANTS.EVENTS.BUSINESS_IMPLICIT_APPLY_COMPLETED, {
          browsingCategoryId,
          targetId,
          targetType,
          timestamp: Date.now(),
        });

        // Focus management: after navigation, focus the newly opened category/tab content
        this._manageFocusAfterImplicitApply(
          targetId,
          targetType,
          triggerElement
        );
      }, 0);
    },

    /**
     * Announces the implicit apply action to screen readers via aria-live region.
     *
     * @private
     * @param {string} browsingCategoryId - The category being implicitly applied
     */
    _announceImplicitApply(browsingCategoryId) {
      // Find the aria-live announcer (provided by liquid template)
      const announcer = document.getElementById('md-implicit-apply-announcer');
      if (!announcer) return; // Gracefully skip if not in DOM

      // Get category label for meaningful announcement
      // Target the category BUTTON explicitly, not a category LAYER: a bare
      // [data-category="..."] also matches the composited image layers, and
      // document order decides which querySelector returns. A layer holds no
      // .category-title, so the announcement silently lost its label.
      const categoryEl =
        document.querySelector(
          `.murphy-door-configurator__category-button[data-category="${browsingCategoryId}"]`
        ) || document.querySelector(`[data-category="${browsingCategoryId}"]`);
      const categoryLabel =
        categoryEl
          ?.querySelector('.murphy-door-configurator__category-title')
          ?.textContent?.trim() || 'Selection';

      // Clear and re-set to force re-announcement
      announcer.textContent = '';
      requestAnimationFrame(() => {
        announcer.textContent = `Saving ${categoryLabel} selection`;
      });
    },

    /**
     * Names where focus is about to go, before _manageFocusAfterImplicitApply
     * moves it.
     *
     * WCAG 3.2.2 On Input: moving focus after a selection IS a change of
     * context, and is only acceptable when the customer has been advised. The
     * existing "Saving X selection" satisfies that in spirit but never says
     * focus is moving or where to -- a screen-reader user is simply somewhere
     * new. This adds the destination.
     *
     * Additive: it does not replace or suppress the "Saving" announcement, it
     * follows it. Fired before the 50ms focus timeout so the sentence is
     * queued ahead of the context change.
     * @private
     * @param {string} targetId
     * @param {'tab'|'category'} targetType
     */
    _announceFocusDestination(targetId, targetType) {
      try {
        const announcer = document.getElementById(
          'md-implicit-apply-announcer'
        );
        if (!announcer || !targetId) return;
        const el =
          targetType === 'tab'
            ? document.querySelector(`[data-tab="${targetId}"]`)
            : document.querySelector(
                `.murphy-door-configurator__category-button[data-category="${targetId}"]`
              ) || document.querySelector(`[data-category="${targetId}"]`);
        const label =
          el
            ?.querySelector('.murphy-door-configurator__category-title')
            ?.textContent?.trim() ||
          el?.textContent?.trim()?.split('\n')[0] ||
          targetId;
        announcer.textContent = `Moving to ${label}`;
      } catch (error) {
        isDev() &&
          console.warn(
            '[EventHandler] _announceFocusDestination failed:',
            error
          );
      }
    },

    /**
     * Manages focus after implicit apply navigation completes.
     * Waits for DOM to settle, then focuses the appropriate element in the
     * newly opened category or tab.
     *
     * @private
     * @param {string} targetId - The target tab or category ID
     * @param {string} targetType - 'tab' or 'category'
     * @param {HTMLElement} triggerElement - The element that triggered the navigation
     */
    _manageFocusAfterImplicitApply(targetId, targetType, triggerElement) {
      // Advise BEFORE the context change, not after it (WCAG 3.2.2).
      this._announceFocusDestination(targetId, targetType);

      // Wait for DOM to settle after navigation
      setTimeout(() => {
        let focusTarget = null;

        if (targetType === 'tab') {
          // NOTE: Tab buttons use data-tab="<tabId>" attribute (set by NavigationManager).
          focusTarget = document.querySelector(`[data-tab="${targetId}"]`);
        } else {
          // Focus the first thumbnail in the newly opened category
          focusTarget = document.querySelector(
            `[data-category="${targetId}"] .murphy-door-configurator__thumbnail-item`
          );
        }

        if (focusTarget) {
          focusTarget.focus({ preventScroll: false });
        }
      }, 50);
    },

    /**
     * Tracks performance metrics for event handlers and publishes performance events when thresholds are exceeded.
     * This method is part of the performance monitoring system in the Central Conductor Pattern.
     *
     * @private
     * @param {string} handlerName - The name of the event handler being tracked
     * @param {number} startTime - The start time of the handler execution
     * @returns {void}
     */
    _trackEventHandlerPerformance(handlerName, startTime) {
      const executionTime =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      // Publish performance event if execution time exceeds threshold
      if (executionTime > PERFORMANCE_THRESHOLDS.HANDLER_EXECUTION) {
        this.publishEvent(CONSTANTS.EVENTS.PERFORMANCE_EVENT_HANDLER_SLOW, {
          handler: handlerName,
          method: 'setupGlobalEventDelegation',
          module: 'eventHandler',
          executionTime: executionTime,
          threshold: PERFORMANCE_THRESHOLDS.HANDLER_EXECUTION,
          timestamp: window.performance ? window.performance.now() : Date.now(),
        });
      }
    },

    /**
     * Triggers the thumbnail apply animation (BROWSING -> APPLIED visual transition).
     * Adds the --applying CSS class which triggers the brand-color fill animation
     * on the circle-check icon, then executes the callback after animation completes.
     *
     * @private
     * @param {HTMLElement} thumbnail - The active thumbnail element
     * @param {Function} callback - Function to execute after animation completes
     */
    _triggerThumbnailApplyAnimation(thumbnail, callback) {
      const circleBg = thumbnail.querySelector('.md-circle-bg');
      if (!circleBg) {
        // No animatable element: execute callback immediately (no animation to wait for)
        isDev() &&
          console.warn(
            '[EventHandler] Could not find .md-circle-bg for apply animation. Applying directly.'
          );
        if (typeof callback === 'function') callback();
        return;
      }

      // Guard: ensure callback fires exactly once regardless of how animation ends
      let callbackFired = false;
      const safeCallback = () => {
        if (callbackFired) return;
        callbackFired = true;
        thumbnail.classList.remove(
          'murphy-door-configurator__thumbnail-item--applying'
        );
        // Restore preview indicator text (safety restore in case category stays open)
        const restoredIndicator = thumbnail.querySelector(
          '.murphy-door-configurator__preview-indicator'
        );
        if (restoredIndicator) restoredIndicator.textContent = 'PREVIEWING';
        if (typeof callback === 'function') callback();
      };

      // Safety timeout: if animation never fires (reduced motion, hidden element, CSS not loaded),
      // proceed after 600ms so the user's apply action is never silently dropped.
      const safetyTimer = setTimeout(() => {
        isDev() &&
          console.warn(
            '[EventHandler] Apply animation timed out (600ms). Proceeding with state change.'
          );
        safeCallback();
      }, 600);

      const onAnimationComplete = () => {
        clearTimeout(safetyTimer);
        safeCallback();
      };

      // Listen for both completion AND cancellation so callback always fires
      circleBg.addEventListener('animationend', onAnimationComplete, {
        once: true,
      });
      circleBg.addEventListener('animationcancel', onAnimationComplete, {
        once: true,
      });

      // Update preview indicator text to "SAVING" during apply animation
      const previewIndicator = thumbnail.querySelector(
        '.murphy-door-configurator__preview-indicator'
      );
      if (previewIndicator) previewIndicator.textContent = 'SAVING';

      thumbnail.classList.add(
        'murphy-door-configurator__thumbnail-item--applying'
      );
    },

    /**
     * PATTERN #15: Cleanup method for resource management.
     * Unsubscribes all tracked event handlers and resets initialization state.
     *
     * @returns {void}
     */
    cleanup() {
      _internalCleanup();
    },

    /**
     * Helper method to publish events through CoreEventBus
     * Pattern from: md-config-bl-material-selector.js
     *
     * @param {string} eventType - Event type constant from CONSTANTS.EVENTS.* (centralized, no local EVENT_TYPES)
     * @param {Object} eventData - Event payload data (CoreEventBus.publish() freezes centrally - no Object.freeze() needed)
     */
    publishEvent(eventType, eventData) {
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          eventType,
          eventData
        );
      } else {
        isDev() &&
          console.warn(
            `[eventHandler] CoreEventBus not available, failed to publish: ${eventType}`
          );
      }
    },

    // ✅ ARCHITECTURAL CLEANUP: getActiveSelection() method removed
    // Event handler should ONLY handle events, not retrieve data.
    // Data retrieval is conductor's responsibility via delegation.
    // This removal eliminates architectural confusion and enforces CCP single responsibility.
  };

  // P2.4: MODERN EVENT HANDLER REGISTRATION
  // Register the modern CCP-compliant event handler with the Central Conductor
  if (window.MurphyDoorConfigurator && window.MurphyDoorConfigurator.main) {
    isDev() &&
      console.log('🔥 Registering modern unified event handler with conductor');

    // Use the module's own helper to publish the event
    window.MurphyDoorConfigurator.eventHandler.publishEvent(
      CONSTANTS.EVENTS.UI_EVENT_HANDLER_INITIALIZED,
      {
        timestamp: Date.now(),
        module: 'eventHandler',
      }
    );
  } else {
    isDev() &&
      console.warn(
        '⚠️ Central Conductor not available, eventHandler initialization may be deferred'
      );

    // Use the module's own helper to publish the event
    window.MurphyDoorConfigurator.eventHandler.publishEvent(
      CONSTANTS.EVENTS.UI_EVENT_HANDLER_INITIALIZATION_DEFERRED,
      {
        timestamp: Date.now(),
        module: 'eventHandler',
        reason: 'conductor_not_available',
      }
    );
  }
})(window);

// P2.4: LEGACY EVENT DELEGATION SYSTEM REMOVED
// The legacy MurphyDoorConfigurator.EventHandler module has been eliminated to prevent
// race conditions and duplicate event handling. All category opening is now handled
// by the modern CCP-compliant event handler system above.
//
// RACE CONDITION FIX: Only one event delegation system remains active:
// - Primary System: window.MurphyDoorConfigurator.eventHandler.setupGlobalEventDelegation()
// - Legacy System: REMOVED (was causing duplicate category toggle events)
//
// This ensures unified category opening behavior across all category types.
